import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  SignIn, 
  Users, 
  User,
  Airplane, 
  ForkKnife, 
  ChartBar, 
  SignOut,
  Check,
  X,
  Trash,
  Plus,
  UserPlus,
  PencilSimple,
  Envelope,
  MapPin,
  UploadSimple,
  DownloadSimple,
  FileText,
  Images,
  ArrowUp,
  ArrowDown,
  House,
  CalendarBlank,
  Gift,
  Gear,
  Bed,
  Question,
  Image,
  ArrowCounterClockwise,
  Bus,
  MapTrifold,
  Sun,
  CheckSquare,
  Copy,
  Heart
} from '@phosphor-icons/react';

const API = '/api';

function QuickEventStatus({ value, onYes, onNo, testId }) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <button
        onClick={onYes}
        className={`px-2 py-1 text-xs rounded transition-colors ${
          value === true
            ? 'bg-green-100 text-green-700'
            : 'bg-stone-100 text-stone-400 hover:bg-green-50 hover:text-green-600'
        }`}
        title="Set Yes"
      >
        <Check size={12} />
      </button>
      <button
        onClick={onNo}
        className={`px-2 py-1 text-xs rounded transition-colors ${
          value === false
            ? 'bg-red-100 text-red-700'
            : 'bg-stone-100 text-stone-400 hover:bg-red-50 hover:text-red-600'
        }`}
        title="Set No"
      >
        <X size={12} />
      </button>
      {value !== true && value !== false && (
        <span className="text-[10px] text-stone-400 ml-0.5 whitespace-nowrap">not answered</span>
      )}
    </div>
  );
}

// Helper function to format timestamps
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  try {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return '-';
  }
};

// Image Positioner Component - Drag to reposition
function ImagePositioner({ imageUrl, position, scale, onPositionChange, onScaleChange }) {
  const containerRef = useRef(null);
  const mobileContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(100);
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop' or 'mobile'

  // Parse position string to x,y percentages
  useEffect(() => {
    if (position) {
      const parts = position.split(' ');
      let x = 50, y = 50;
      
      if (parts[0]) {
        if (parts[0] === 'left') x = 0;
        else if (parts[0] === 'center') x = 50;
        else if (parts[0] === 'right') x = 100;
        else x = parseFloat(parts[0]) || 50;
      }
      
      if (parts[1]) {
        if (parts[1] === 'top') y = 0;
        else if (parts[1] === 'center') y = 50;
        else if (parts[1] === 'bottom') y = 100;
        else y = parseFloat(parts[1]) || 50;
      }
      
      setCurrentPos({ x, y });
    }
  }, [position]);

  // Parse scale
  useEffect(() => {
    if (scale) {
      if (scale === 'cover') setZoom(100);
      else if (scale === 'contain') setZoom(80);
      else setZoom(parseFloat(scale) || 100);
    }
  }, [scale]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const activeRef = viewMode === 'desktop' ? containerRef : mobileContainerRef;
    if (!activeRef.current) return;
    
    const rect = activeRef.current.getBoundingClientRect();
    const deltaX = (e.clientX - dragStart.x) / rect.width * 100;
    const deltaY = (e.clientY - dragStart.y) / rect.height * 100;
    
    const newX = Math.max(0, Math.min(100, currentPos.x - deltaX));
    const newY = Math.max(0, Math.min(100, currentPos.y - deltaY));
    
    setCurrentPos({ x: newX, y: newY });
    setDragStart({ x: e.clientX, y: e.clientY });
    onPositionChange(`${Math.round(newX)}% ${Math.round(newY)}%`);
  }, [isDragging, dragStart, currentPos, onPositionChange, viewMode]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleZoomChange = (newZoom) => {
    setZoom(newZoom);
    if (newZoom === 100) {
      onScaleChange('cover');
    } else {
      onScaleChange(`${newZoom}%`);
    }
  };

  const handleQuickPosition = (x, y) => {
    setCurrentPos({ x, y });
    onPositionChange(`${x}% ${y}%`);
  };

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between border-b pb-3">
        <Label className="font-body text-sm font-medium">Preview & Adjust Position</Label>
        <div className="flex gap-1 bg-stone-100 p-1 rounded">
          <button
            type="button"
            onClick={() => setViewMode('desktop')}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              viewMode === 'desktop' 
                ? 'bg-white text-stone-900 shadow-sm' 
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            Desktop
          </button>
          <button
            type="button"
            onClick={() => setViewMode('mobile')}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              viewMode === 'mobile' 
                ? 'bg-white text-stone-900 shadow-sm' 
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            Mobile
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-stone-500">
          {viewMode === 'desktop' ? '16:9 Landscape View' : '9:16 Portrait View'}
        </span>
        <span className="text-xs text-stone-500">
          Position: {Math.round(currentPos.x)}% horizontal, {Math.round(currentPos.y)}% vertical
        </span>
      </div>
      
      {/* Desktop Preview Frame - 16:9 aspect ratio */}
      {viewMode === 'desktop' && (
        <div 
          ref={containerRef}
          className="relative w-full bg-stone-900 overflow-hidden cursor-move border-4 border-[#C9B99A]"
          style={{ aspectRatio: '16/9' }}
          onMouseDown={handleMouseDown}
        >
          <div 
            className="absolute inset-0 transition-none"
            style={{
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: zoom === 100 ? 'cover' : `${zoom}%`,
              backgroundPosition: `${currentPos.x}% ${currentPos.y}%`,
              backgroundRepeat: 'no-repeat',
            }}
          />
          
          {/* Center guides */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/30" />
            <div className="absolute top-1/2 left-0 right-0 h-px bg-white/30" />
          </div>
          
          {/* Text overlay preview */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-white">
              <p className="text-xs tracking-widest opacity-60 mb-1">DECEMBER 11, 2026</p>
              <h1 className="text-2xl md:text-4xl font-serif">
                Stefanie <span className="italic text-[#C9B99A]">&</span> Walter
              </h1>
            </div>
          </div>
          
          {isDragging && (
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
              <span className="bg-black/70 text-white px-3 py-1 rounded text-sm">
                Dragging...
              </span>
            </div>
          )}
        </div>
      )}

      {/* Mobile Preview Frame - 9:16 aspect ratio */}
      {viewMode === 'mobile' && (
        <div className="flex justify-center">
          <div 
            ref={mobileContainerRef}
            className="relative bg-stone-900 overflow-hidden cursor-move border-4 border-[#C9B99A] rounded-3xl"
            style={{ width: '220px', height: '390px' }}
            onMouseDown={handleMouseDown}
          >
            {/* Phone notch */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full z-20" />
            
            <div 
              className="absolute inset-0 transition-none"
              style={{
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: zoom === 100 ? 'cover' : `${zoom}%`,
                backgroundPosition: `${currentPos.x}% ${currentPos.y}%`,
                backgroundRepeat: 'no-repeat',
              }}
            />
            
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/40" />
            
            {/* Center guides */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/30" />
              <div className="absolute top-1/2 left-0 right-0 h-px bg-white/30" />
            </div>
            
            {/* Text overlay preview - mobile sized */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center text-white px-4">
                <p className="text-[8px] tracking-widest opacity-60 mb-1">DECEMBER 11, 2026</p>
                <h1 className="text-lg font-serif">
                  Stefanie <span className="italic text-[#C9B99A]">&</span> Walter
                </h1>
              </div>
            </div>
            
            {isDragging && (
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
                <span className="bg-black/70 text-white px-2 py-1 rounded text-xs">
                  Dragging...
                </span>
              </div>
            )}
            
            {/* Home indicator */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-white/50 rounded-full" />
          </div>
        </div>
      )}

      {/* Quick Position Buttons */}
      <div className="space-y-2">
        <Label className="font-body text-xs text-stone-500">Quick Position</Label>
        <div className="grid grid-cols-5 gap-1">
          <button
            type="button"
            onClick={() => handleQuickPosition(0, 0)}
            className="p-2 text-xs border border-stone-200 hover:bg-stone-100 rounded"
          >
            ↖ Top Left
          </button>
          <button
            type="button"
            onClick={() => handleQuickPosition(50, 0)}
            className="p-2 text-xs border border-stone-200 hover:bg-stone-100 rounded"
          >
            ↑ Top
          </button>
          <button
            type="button"
            onClick={() => handleQuickPosition(100, 0)}
            className="p-2 text-xs border border-stone-200 hover:bg-stone-100 rounded"
          >
            ↗ Top Right
          </button>
          <button
            type="button"
            onClick={() => handleQuickPosition(0, 50)}
            className="p-2 text-xs border border-stone-200 hover:bg-stone-100 rounded"
          >
            ← Left
          </button>
          <button
            type="button"
            onClick={() => handleQuickPosition(50, 50)}
            className="p-2 text-xs border border-stone-200 hover:bg-stone-100 rounded bg-[#F0EBE3]"
          >
            Center
          </button>
        </div>
        <div className="grid grid-cols-5 gap-1">
          <button
            type="button"
            onClick={() => handleQuickPosition(100, 50)}
            className="p-2 text-xs border border-stone-200 hover:bg-stone-100 rounded"
          >
            → Right
          </button>
          <button
            type="button"
            onClick={() => handleQuickPosition(0, 100)}
            className="p-2 text-xs border border-stone-200 hover:bg-stone-100 rounded"
          >
            ↙ Bottom Left
          </button>
          <button
            type="button"
            onClick={() => handleQuickPosition(50, 100)}
            className="p-2 text-xs border border-stone-200 hover:bg-stone-100 rounded"
          >
            ↓ Bottom
          </button>
          <button
            type="button"
            onClick={() => handleQuickPosition(100, 100)}
            className="p-2 text-xs border border-stone-200 hover:bg-stone-100 rounded"
          >
            ↘ Bottom Right
          </button>
          <div></div>
        </div>
      </div>

      {/* Zoom Slider */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="font-body text-sm">Zoom Level</Label>
          <span className="text-xs text-stone-500">{zoom}%</span>
        </div>
        <input
          type="range"
          min="50"
          max="150"
          value={zoom}
          onChange={(e) => handleZoomChange(parseInt(e.target.value))}
          className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-[#C9B99A]"
        />
        <div className="flex justify-between text-xs text-stone-400">
          <span>Zoom Out (50%)</span>
          <span>Normal (100%)</span>
          <span>Zoom In (150%)</span>
        </div>
      </div>

      {/* Fine-tune controls */}
      <div className="grid grid-cols-2 gap-4 pt-2 border-t">
        <div className="space-y-2">
          <Label className="font-body text-xs text-stone-500">Horizontal Position</Label>
          <input
            type="range"
            min="0"
            max="100"
            value={currentPos.x}
            onChange={(e) => {
              const newX = parseInt(e.target.value);
              setCurrentPos({ ...currentPos, x: newX });
              onPositionChange(`${newX}% ${Math.round(currentPos.y)}%`);
            }}
            className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-[#C9B99A]"
          />
          <div className="flex justify-between text-xs text-stone-400">
            <span>Left</span>
            <span>Center</span>
            <span>Right</span>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="font-body text-xs text-stone-500">Vertical Position</Label>
          <input
            type="range"
            min="0"
            max="100"
            value={currentPos.y}
            onChange={(e) => {
              const newY = parseInt(e.target.value);
              setCurrentPos({ ...currentPos, y: newY });
              onPositionChange(`${Math.round(currentPos.x)}% ${newY}%`);
            }}
            className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-[#C9B99A]"
          />
          <div className="flex justify-between text-xs text-stone-400">
            <span>Top</span>
            <span>Center</span>
            <span>Bottom</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  
  // CSV Upload
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Add Guest Modal
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [guestForm, setGuestForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    plus_ones_allowed: 0,
    plus_one_names: []
  });

  // Edit Address Modal
  const [showEditAddress, setShowEditAddress] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [showEditRsvp, setShowEditRsvp] = useState(false);
  const [editingRsvp, setEditingRsvp] = useState(null);
  const [rsvpForm, setRsvpForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    attending: true,
    attending_welcome_party: null,
    attending_beach_day: null,
    meal_choice: '',
    dietary_restrictions: '',
    song_request: '',
    message: '',
    plus_ones: []
  });

  // Email Guests
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailGroup, setEmailGroup] = useState('not_rsvped');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  const EMAIL_TEMPLATES = {
    not_rsvped: {
      label: "Haven't RSVPed",
      subject: "Please RSVP — Stefanie & Walter's Wedding | December 11, 2026",
      body: `Hello!

We're so excited to celebrate our wedding in El Salvador on December 11, 2026, and we'd love to know if you can join us!

If you haven't had a chance yet, please RSVP on our website:

https://stefanieandwalter2026.com/rsvp
Website password: Arya2026

Please respond by October 16, 2026 so we can finalize our plans.

With love,
Stefanie & Walter`
    },
    wp_unanswered: {
      label: 'Event Questions Not Answered',
      subject: "Quick Question — Welcome Party & Beach Day RSVP | Stefanie & Walter",
      body: `Hello!

Thank you for RSVPing to our wedding — we can't wait to celebrate with you!

One quick thing: we're also hosting a Welcome Party on December 10 and a Beach Day & Overnight in El Tunco on Saturday, December 12, and we don't have your answer for these yet. It only takes a minute on our quick form:

https://stefanieandwalter2026.com/events-rsvp
Website password: Arya2026

Just look up your name and answer yes or no for each event!

With love,
Stefanie & Walter`
    },
    all: {
      label: 'All Guests',
      subject: "Stefanie & Walter's Wedding — December 11, 2026",
      body: `Hello!

We wanted to share an update about our wedding celebration in El Salvador on December 11, 2026.

You can find all the details — travel, hotels, itinerary, and RSVP — on our website:

https://stefanieandwalter2026.com
Website password: Arya2026

With love,
Stefanie & Walter`
    }
  };

  const getEmailRecipients = (group) => {
    if (!dashboardData) return [];
    const emails = new Set();
    if (group === 'not_rsvped') {
      const rsvpEmails = new Set(dashboardData.rsvps.map(r => (r.email || '').toLowerCase()));
      dashboardData.invited_guests.forEach(g => {
        const em = (g.email || '').toLowerCase();
        if (em && !g.has_rsvped && !rsvpEmails.has(em)) emails.add(em);
      });
    } else if (group === 'wp_unanswered') {
      dashboardData.rsvps.forEach(r => {
        if (!r.attending || !r.email) return;
        const primaryUnanswered = typeof r.attending_welcome_party !== 'boolean' || typeof r.attending_beach_day !== 'boolean';
        const plusOneUnanswered = (r.plus_ones || []).some(p => p.attending !== false && (typeof p.attending_welcome_party !== 'boolean' || typeof p.attending_beach_day !== 'boolean'));
        if (primaryUnanswered || plusOneUnanswered) emails.add(r.email.toLowerCase());
      });
    } else {
      dashboardData.invited_guests.forEach(g => {
        if (g.email) emails.add(g.email.toLowerCase());
      });
    }
    return [...emails];
  };

  const openEmailDialog = (group) => {
    setEmailGroup(group);
    setEmailSubject(EMAIL_TEMPLATES[group].subject);
    setEmailBody(EMAIL_TEMPLATES[group].body);
    setShowEmailDialog(true);
  };

  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    plus_ones: []
  });

  // Slideshow Management
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [showAddImage, setShowAddImage] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [imageForm, setImageForm] = useState({
    url: '',
    position: 'center center',
    scale: 'cover'
  });

  // Website Content Management
  const [homepageContent, setHomepageContent] = useState(null);
  const [itineraryContent, setItineraryContent] = useState(null);
  const [registryContent, setRegistryContent] = useState(null);
  const [rsvpContent, setRsvpContent] = useState(null);
  const [stayContent, setStayContent] = useState(null);
  const [bridalContent, setBridalContent] = useState(null);
  const [travelContent, setTravelContent] = useState(null);
  const [faqContent, setFaqContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  useEffect(() => {
    const savedToken = localStorage.getItem('wedding_admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchDashboardData();
    }
  }, [isLoggedIn, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      try {
        await axios.post(`${API}/admin/setup`);
      } catch (e) {}

      const response = await axios.post(`${API}/admin/login`, {
        username,
        password,
      });

      const { token: newToken } = response.data;
      setToken(newToken);
      localStorage.setItem('wedding_admin_token', newToken);
      setIsLoggedIn(true);
      toast.success('Welcome back!');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials. Default: admin / wedding2026');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('wedding_admin_token');
    setIsLoggedIn(false);
    setDashboardData(null);
  };

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardData(response.data);
      
      // Also fetch slideshow images
      fetchSlideshowImages();
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    }
  };

  // Slideshow functions
  const fetchSlideshowImages = async () => {
    try {
      // First, try to initialize with default images if empty
      await axios.post(`${API}/admin/slideshow/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Then fetch all images
      const response = await axios.get(`${API}/admin/slideshow`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSlideshowImages(response.data);
    } catch (error) {
      console.error('Slideshow fetch error:', error);
    }
  };

  const handleAddImage = async () => {
    if (!imageForm.url) {
      toast.error('Please enter an image URL');
      return;
    }

    setIsLoading(true);
    try {
      if (editingImage) {
        await axios.put(`${API}/admin/slideshow/${editingImage.id}`, imageForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Image updated');
      } else {
        await axios.post(`${API}/admin/slideshow`, imageForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Image added to slideshow');
      }
      setShowAddImage(false);
      setEditingImage(null);
      setImageForm({ url: '', position: 'center center', scale: 'cover' });
      fetchSlideshowImages();
    } catch (error) {
      toast.error('Failed to save image');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image from the slideshow?')) return;
    
    try {
      await axios.delete(`${API}/admin/slideshow/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Image deleted');
      fetchSlideshowImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const moveImage = async (index, direction) => {
    const newImages = [...slideshowImages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newImages.length) return;
    
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    
    try {
      await axios.post(`${API}/admin/slideshow/reorder`, 
        newImages.map(img => img.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlideshowImages(newImages);
      toast.success('Images reordered');
    } catch (error) {
      toast.error('Failed to reorder images');
    }
  };

  const openEditImage = (image) => {
    setEditingImage(image);
    setImageForm({
      url: image.url,
      position: image.position || 'center center',
      scale: image.scale || 'cover'
    });
    setShowAddImage(true);
  };

  // Website Content Functions
  const fetchContent = async (section, setter) => {
    try {
      const response = await axios.get(`${API}/admin/content/${section}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setter(response.data.content);
    } catch (error) {
      console.error(`Failed to fetch ${section} content:`, error);
    }
  };

  const saveContent = async (section, content) => {
    setContentLoading(true);
    try {
      await axios.put(`${API}/admin/content/${section}`, 
        { section, content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} content saved!`);
    } catch (error) {
      toast.error(`Failed to save ${section} content`);
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch content when tabs change
  const handleTabChange = (tab) => {
    if (tab === 'homepage' && !homepageContent) {
      fetchContent('homepage', setHomepageContent);
    } else if (tab === 'itinerary-edit' && !itineraryContent) {
      fetchContent('itinerary', setItineraryContent);
    } else if (tab === 'registry-edit' && !registryContent) {
      fetchContent('registry', setRegistryContent);
    } else if (tab === 'rsvp-edit' && !rsvpContent) {
      fetchContent('rsvp', setRsvpContent);
    } else if (tab === 'stay-edit' && !stayContent) {
      fetchContent('accommodations', setStayContent);
    } else if (tab === 'travel-edit' && !travelContent) {
      fetchContent('travel', setTravelContent);
    } else if (tab === 'faq-edit' && !faqContent) {
      fetchContent('faq', setFaqContent);
    } else if (tab === 'bridal-edit' && !bridalContent) {
      fetchContent('bridal_party', setBridalContent);
    }
  };

  const handleAddGuest = async () => {
    if (!guestForm.first_name || !guestForm.last_name) {
      toast.error('Please fill in first and last name');
      return;
    }

    const payload = {
      ...guestForm,
      email: (guestForm.email || '').trim(),
      plus_one_names: (guestForm.plus_one_names || []).map(n => n.trim()).filter(Boolean)
    };

    setIsLoading(true);
    try {
      if (editingGuest) {
        await axios.put(`${API}/invited-guests/${editingGuest.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Guest updated');
      } else {
        await axios.post(`${API}/invited-guests`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Guest added to invite list');
      }
      setShowAddGuest(false);
      setEditingGuest(null);
      setGuestForm({ first_name: '', last_name: '', email: '', plus_ones_allowed: 0, plus_one_names: [] });
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save guest');
    } finally {
      setIsLoading(false);
    }
  };

  // Open edit address modal
  const openEditAddress = (addr) => {
    setEditingAddress(addr);
    setAddressForm({
      first_name: addr.first_name || '',
      last_name: addr.last_name || '',
      email: addr.email || '',
      phone: addr.phone || '',
      address_line1: addr.address_line1 || '',
      address_line2: addr.address_line2 || '',
      city: addr.city || '',
      state: addr.state || '',
      zip_code: addr.zip_code || '',
      country: addr.country || 'United States',
      plus_ones: addr.plus_ones || []
    });
    setShowEditAddress(true);
  };

  // Handle save address
  const handleSaveAddress = async () => {
    if (!addressForm.first_name || !addressForm.last_name || !addressForm.email) {
      toast.error('Please fill in name and email');
      return;
    }

    setIsLoading(true);
    try {
      await axios.put(`${API}/addresses/${editingAddress.id}`, addressForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Address updated successfully');
      setShowEditAddress(false);
      setEditingAddress(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update address');
    } finally {
      setIsLoading(false);
    }
  };

  // Add household member to address form
  const addHouseholdMember = () => {
    setAddressForm({
      ...addressForm,
      plus_ones: [...addressForm.plus_ones, { name: '', email: '', phone: '' }]
    });
  };

  // Remove household member from address form
  const removeHouseholdMember = (index) => {
    setAddressForm({
      ...addressForm,
      plus_ones: addressForm.plus_ones.filter((_, i) => i !== index)
    });
  };

  // Update household member in address form
  const updateHouseholdMember = (index, field, value) => {
    const updated = [...addressForm.plus_ones];
    updated[index][field] = value;
    setAddressForm({ ...addressForm, plus_ones: updated });
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/invited-guests/upload-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      setUploadResult(response.data);
      toast.success(response.data.message);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setIsLoading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const downloadTemplate = () => {
    const csv = 'first_name,last_name,email,plus_ones_allowed\nJohn,Smith,john@example.com,1\nJane,Doe,jane@example.com,0';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'guest_list_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportAddresses = () => {
    if (!dashboardData?.addresses?.length) {
      toast.error('No addresses to export');
      return;
    }
    // Headers include household_members (semicolon-separated) AND individual columns for full round-trip
    const headers = [
      'first_name', 'last_name', 'email', 'phone', 
      'address_line1', 'address_line2', 'city', 'state', 'zip_code', 'country',
      'household_members',
      'household1_name', 'household1_email', 'household1_phone',
      'household2_name', 'household2_email', 'household2_phone',
      'household3_name', 'household3_email', 'household3_phone',
      'household4_name', 'household4_email', 'household4_phone',
      'household5_name', 'household5_email', 'household5_phone'
    ];
    const csv = [
      headers.join(','),
      ...dashboardData.addresses.map(addr => {
        const baseFields = ['first_name', 'last_name', 'email', 'phone', 'address_line1', 'address_line2', 'city', 'state', 'zip_code', 'country']
          .map(h => `"${(addr[h] || '').replace(/"/g, '""')}"`);
        
        // Add semicolon-separated household members for easy viewing
        const householdNames = (addr.plus_ones || []).map(p => p.name).join('; ');
        baseFields.push(`"${householdNames}"`);
        
        // Add individual household member fields (up to 5) for full data preservation
        for (let i = 0; i < 5; i++) {
          const member = addr.plus_ones && addr.plus_ones[i];
          baseFields.push(`"${(member?.name || '').replace(/"/g, '""')}"`);
          baseFields.push(`"${(member?.email || '').replace(/"/g, '""')}"`);
          baseFields.push(`"${(member?.phone || '').replace(/"/g, '""')}"`);
        }
        
        return baseFields.join(',');
      })
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'collected_addresses.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Addresses exported!');
  };

  const handleAddressCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/addresses/upload-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      toast.success(response.data.message);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setIsLoading(false);
      e.target.value = '';
    }
  };

  const downloadAddressTemplate = () => {
    const headers = [
      'first_name', 'last_name', 'email', 'phone', 
      'address_line1', 'address_line2', 'city', 'state', 'zip_code', 'country',
      'household1_name', 'household1_email', 'household1_phone',
      'household2_name', 'household2_email', 'household2_phone',
      'household3_name', 'household3_email', 'household3_phone'
    ];
    const exampleRow = [
      'John', 'Smith', 'john@example.com', '555-123-4567',
      '123 Main St', 'Apt 4B', 'New York', 'NY', '10001', 'United States',
      'Jane Smith', 'jane@example.com', '555-234-5678',
      'Bobby Smith', 'bobby@example.com', '555-345-6789',
      '', '', ''
    ];
    const csv = [headers.join(','), exampleRow.map(v => `"${v}"`).join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'address_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleEditGuest = (guest) => {
    setEditingGuest(guest);
    setGuestForm({
      first_name: guest.first_name,
      last_name: guest.last_name,
      email: guest.email || '',
      plus_ones_allowed: guest.plus_ones_allowed,
      plus_one_names: guest.plus_one_names || []
    });
    setShowAddGuest(true);
  };

  const setRsvpAttending = async (rsvp, value) => {
    try {
      await axios.put(`${API}/admin/rsvp/${rsvp.id}`,
        { attending: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${rsvp.first_name}'s RSVP set to ${value ? 'Attending' : 'Declined'}`);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const setGuestEventStatus = async (guest, field, value) => {
    try {
      await axios.put(`${API}/admin/guest/${guest.id}/rsvp-status`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${guest.first_name}'s status updated`);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const setMemberEventStatus = async (guest, memberName, field, value) => {
    try {
      await axios.put(`${API}/admin/household-member/rsvp-status`,
        { member_name: memberName, main_guest_id: guest.id, [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${memberName}'s status updated`);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteGuest = async (guestId) => {
    if (!window.confirm('Are you sure you want to remove this guest from the invite list?')) return;
    
    try {
      await axios.delete(`${API}/invited-guests/${guestId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Guest removed');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete guest');
    }
  };

  const handleDeleteRSVP = async (rsvpId) => {
    if (!window.confirm('Are you sure you want to delete this RSVP?')) return;
    
    try {
      await axios.delete(`${API}/admin/rsvp/${rsvpId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('RSVP deleted');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete RSVP');
    }
  };

  if (!isLoggedIn) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white border border-stone-200 p-8">
            <div className="text-center mb-8">
              <SignIn size={48} weight="light" className="text-[#B8A88A] mx-auto mb-4" />
              <h1 className="font-display text-2xl text-stone-900">Admin Login</h1>
              <p className="font-body text-sm text-stone-500 mt-2">
                Access the wedding dashboard
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="username" className="font-body text-sm text-stone-700">
                  Username
                </Label>
                <Input
                  id="username"
                  data-testid="input-admin-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-white border-stone-200 focus:border-[#C9B99A]"
                  placeholder="Enter username"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-body text-sm text-stone-700">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="input-admin-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-white border-stone-200 focus:border-[#C9B99A]"
                  placeholder="Enter password"
                />
              </div>

              <Button
                type="submit"
                data-testid="admin-login-btn"
                disabled={isLoading}
                className="w-full bg-[#C9B99A] hover:bg-[#B8A88A] text-white py-6"
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-[#C9B99A] text-white py-6">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl">Wedding Dashboard</h1>
            <p className="font-body text-sm text-stone-300">
              Manage guests, RSVPs, flights, and meals
            </p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            variant="outline"
            className="bg-transparent border-white text-white hover:bg-white hover:text-[#B8A88A]"
          >
            <SignOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {dashboardData && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <div className="bg-white border border-stone-200 p-6">
              <p className="font-body text-sm text-stone-500 uppercase tracking-wide">Addresses</p>
              <p className="font-display text-3xl text-[#B8A88A] mt-2">
                {dashboardData.summary.addresses_collected || 0}
              </p>
            </div>
            <div className="bg-white border border-stone-200 p-6">
              <p className="font-body text-sm text-stone-500 uppercase tracking-wide">Invited</p>
              <p className="font-display text-3xl text-stone-900 mt-2">
                {dashboardData.summary.total_invited}
              </p>
            </div>
            <div className="bg-white border border-stone-200 p-6">
              <p className="font-body text-sm text-stone-500 uppercase tracking-wide">Responded</p>
              <p className="font-display text-3xl text-[#B8A88A] mt-2">
                {dashboardData.summary.total_rsvps}
              </p>
            </div>
            <div className="bg-white border border-stone-200 p-6">
              <p className="font-body text-sm text-stone-500 uppercase tracking-wide">Declined</p>
              <p className="font-display text-3xl text-stone-400 mt-2">
                {dashboardData.summary.not_attending}
              </p>
            </div>
            <div className="bg-white border border-[#C9B99A] p-6" data-testid="stat-wedding-guests">
              <p className="font-body text-sm text-[#B8A88A] uppercase tracking-wide">Wedding Guests</p>
              <p className="font-display text-3xl text-[#B8A88A] mt-2">
                {dashboardData.summary.wedding_guests}
              </p>
              <p className="font-body text-xs text-stone-400 mt-1">total people attending</p>
            </div>
            <div className="bg-white border border-[#C9B99A] p-6" data-testid="stat-welcome-party-guests">
              <p className="font-body text-sm text-[#B8A88A] uppercase tracking-wide">Welcome Party</p>
              <p className="font-display text-3xl text-[#B8A88A] mt-2">
                {dashboardData.summary.welcome_party_guests}
              </p>
              {dashboardData.summary.welcome_party_not_answered > 0 ? (
                <p className="font-body text-xs text-amber-600 mt-1">{dashboardData.summary.welcome_party_not_answered} not answered</p>
              ) : (
                <p className="font-body text-xs text-stone-400 mt-1">total people attending</p>
              )}
            </div>
            <div className="bg-white border border-[#C9B99A] p-6" data-testid="stat-beach-day-guests">
              <p className="font-body text-sm text-[#B8A88A] uppercase tracking-wide">Beach Day</p>
              <p className="font-display text-3xl text-[#B8A88A] mt-2">
                {dashboardData.summary.beach_day_guests}
              </p>
              {dashboardData.summary.beach_day_not_answered > 0 ? (
                <p className="font-body text-xs text-amber-600 mt-1">{dashboardData.summary.beach_day_not_answered} not answered</p>
              ) : (
                <p className="font-body text-xs text-stone-400 mt-1">total people attending</p>
              )}
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="addresses" className="space-y-6" onValueChange={handleTabChange}>
            <TabsList className="bg-white border border-stone-200 p-1 flex-wrap">
              <TabsTrigger value="addresses" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <MapPin size={18} className="mr-2" />
                Addresses
              </TabsTrigger>
              <TabsTrigger value="guests" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <UserPlus size={18} className="mr-2" />
                Guest List
              </TabsTrigger>
              <TabsTrigger value="rsvps" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <Envelope size={18} className="mr-2" />
                RSVPs
              </TabsTrigger>
              <TabsTrigger value="flights" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <Airplane size={18} className="mr-2" />
                Travel Info
              </TabsTrigger>
              <TabsTrigger value="meals" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <ForkKnife size={18} className="mr-2" />
                Meals
              </TabsTrigger>
              <TabsTrigger value="slideshow" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <Images size={18} className="mr-2" />
                Slideshow
              </TabsTrigger>
              <TabsTrigger value="homepage" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <House size={18} className="mr-2" />
                Homepage
              </TabsTrigger>
              <TabsTrigger value="itinerary-edit" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <CalendarBlank size={18} className="mr-2" />
                Itinerary
              </TabsTrigger>
              <TabsTrigger value="registry-edit" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <Gift size={18} className="mr-2" />
                Registry
              </TabsTrigger>
              <TabsTrigger value="rsvp-edit" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <Gear size={18} className="mr-2" />
                RSVP Settings
              </TabsTrigger>
              <TabsTrigger value="stay-edit" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <Bed size={18} className="mr-2" />
                Stay
              </TabsTrigger>
              <TabsTrigger value="travel-edit" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <Airplane size={18} className="mr-2" />
                Travel
              </TabsTrigger>
              <TabsTrigger value="faq-edit" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <Question size={18} className="mr-2" />
                FAQ
              </TabsTrigger>
              <TabsTrigger value="bridal-edit" data-testid="bridal-party-tab" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <Heart size={18} className="mr-2" />
                Bridal Party
              </TabsTrigger>
              <TabsTrigger value="stats" className="font-body data-[state=active]:bg-[#C9B99A] data-[state=active]:text-white">
                <ChartBar size={18} className="mr-2" />
                Stats
              </TabsTrigger>
            </TabsList>

            {/* Addresses Tab */}
            <TabsContent value="addresses">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200 flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className="font-display text-lg">Collected Addresses</h3>
                    <p className="font-body text-sm text-stone-500">
                      Share link: <span className="text-[#B8A88A]">/address</span>
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {/* Add Address Button */}
                    <Button
                      className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                      onClick={() => {
                        setAddressForm({
                          first_name: '',
                          last_name: '',
                          email: '',
                          phone: '',
                          address_line1: '',
                          address_line2: '',
                          city: '',
                          state: '',
                          zip_code: '',
                          country: 'United States',
                          plus_ones: []
                        });
                        setShowAddAddress(true);
                      }}
                    >
                      <Plus size={18} className="mr-2" />
                      Add Address
                    </Button>
                    
                    {/* CSV Upload */}
                    <div className="relative">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleAddressCSVUpload}
                        className="hidden"
                        id="address-csv-upload"
                      />
                      <Button
                        variant="outline"
                        className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                        onClick={() => document.getElementById('address-csv-upload').click()}
                        disabled={isLoading}
                      >
                        <UploadSimple size={18} className="mr-2" />
                        Upload CSV
                      </Button>
                    </div>
                    
                    {/* Download Template */}
                    <Button
                      variant="outline"
                      className="border-stone-300 text-stone-600 hover:bg-stone-50"
                      onClick={downloadAddressTemplate}
                    >
                      <FileText size={18} className="mr-2" />
                      Template
                    </Button>
                    
                    {/* Export */}
                    <Button
                      onClick={exportAddresses}
                      variant="outline"
                      className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                    >
                      <DownloadSimple size={18} className="mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>City, State ZIP</TableHead>
                      <TableHead>Household Members</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(!dashboardData.addresses || dashboardData.addresses.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-stone-500">
                          No addresses collected yet. Share the /address link to collect addresses.
                        </TableCell>
                      </TableRow>
                    ) : (
                      dashboardData.addresses.map((addr) => (
                        <TableRow key={addr.id}>
                          <TableCell className="font-medium">
                            {addr.first_name} {addr.last_name}
                          </TableCell>
                          <TableCell>{addr.email}</TableCell>
                          <TableCell>{addr.phone || '-'}</TableCell>
                          <TableCell>
                            {addr.address_line1}
                            {addr.address_line2 && <>, {addr.address_line2}</>}
                          </TableCell>
                          <TableCell>
                            {addr.city}, {addr.state} {addr.zip_code}
                          </TableCell>
                          <TableCell>
                            {addr.plus_ones && addr.plus_ones.length > 0 ? (
                              <div className="space-y-1">
                                {addr.plus_ones.map((person, i) => (
                                  <div key={i} className="text-sm">
                                    <span className="font-medium">{person.name}</span>
                                    {person.email && (
                                      <span className="text-stone-400 text-xs block">{person.email}</span>
                                    )}
                                    {person.phone && (
                                      <span className="text-stone-400 text-xs block">{person.phone}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-stone-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-stone-500 whitespace-nowrap">
                              {formatTimestamp(addr.created_at)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <button
                                onClick={() => openEditAddress(addr)}
                                className="text-stone-500 hover:text-[#B8A88A]"
                                title="Edit"
                              >
                                <PencilSimple size={18} />
                              </button>
                              <button
                                onClick={async () => {
                                  if (window.confirm('Delete this address?')) {
                                    try {
                                      await axios.delete(`${API}/addresses/${addr.id}`, {
                                        headers: { Authorization: `Bearer ${token}` },
                                      });
                                      toast.success('Address deleted');
                                      fetchDashboardData();
                                    } catch (e) {
                                      toast.error('Failed to delete');
                                    }
                                  }
                                }}
                                className="text-stone-500 hover:text-red-500"
                                title="Delete"
                              >
                                <Trash size={18} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Guest List Tab */}
            <TabsContent value="guests">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200">
                  <div className="flex justify-between items-center flex-wrap gap-4">
                    <h3 className="font-display text-lg">Invited Guests</h3>
                    <div className="flex gap-2 flex-wrap">
                      {/* Sync from Addresses */}
                      <Button
                        variant="outline"
                        className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                        onClick={async () => {
                          if (window.confirm('This will sync all address submissions to the guest list. Continue?')) {
                            setIsLoading(true);
                            try {
                              const response = await axios.post(`${API}/admin/sync-addresses-to-guests`, {}, {
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              toast.success(response.data.message);
                              fetchDashboardData();
                            } catch (error) {
                              toast.error('Failed to sync addresses');
                            } finally {
                              setIsLoading(false);
                            }
                          }
                        }}
                        disabled={isLoading}
                      >
                        <Users size={18} className="mr-2" />
                        Sync from Addresses
                      </Button>

                      {/* Email Guests */}
                      <Button
                        data-testid="email-guests-btn-guests"
                        variant="outline"
                        className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                        onClick={() => openEmailDialog('not_rsvped')}
                      >
                        <Envelope size={18} className="mr-2" />
                        Email Guests
                      </Button>

                      {/* CSV Upload */}
                      <div className="relative">
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleCSVUpload}
                          className="hidden"
                          id="csv-upload"
                        />
                        <Button
                          variant="outline"
                          className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                          onClick={() => document.getElementById('csv-upload').click()}
                          disabled={isLoading}
                        >
                          <UploadSimple size={18} className="mr-2" />
                          Upload CSV
                        </Button>
                      </div>
                      
                      {/* Download Template */}
                      <Button
                        variant="outline"
                        className="border-stone-300 text-stone-600 hover:bg-stone-50"
                        onClick={downloadTemplate}
                      >
                        <FileText size={18} className="mr-2" />
                        Template
                      </Button>
                      
                      {/* Add Single Guest */}
                      <Dialog open={showAddGuest} onOpenChange={(open) => {
                        setShowAddGuest(open);
                        if (!open) {
                          setEditingGuest(null);
                          setGuestForm({ first_name: '', last_name: '', email: '', plus_ones_allowed: 0, plus_one_names: [] });
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button data-testid="add-guest-btn" className="bg-[#C9B99A] hover:bg-[#B8A88A]">
                            <Plus size={18} className="mr-2" />
                            Add Guest
                          </Button>
                        </DialogTrigger>
                    <DialogContent className="bg-white">
                      <DialogHeader>
                        <DialogTitle className="font-display text-xl">
                          {editingGuest ? 'Edit Guest' : 'Add New Guest'}
                        </DialogTitle>
                        <DialogDescription className="font-body text-sm text-stone-500">
                          Email is optional — guests without one can still be managed and marked by you.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="font-body text-sm">First Name *</Label>
                            <Input
                              data-testid="guest-first-name"
                              value={guestForm.first_name}
                              onChange={(e) => setGuestForm({...guestForm, first_name: e.target.value})}
                              placeholder="First name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-body text-sm">Last Name *</Label>
                            <Input
                              data-testid="guest-last-name"
                              value={guestForm.last_name}
                              onChange={(e) => setGuestForm({...guestForm, last_name: e.target.value})}
                              placeholder="Last name"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-sm">Email Address <span className="text-stone-400">(optional)</span></Label>
                          <Input
                            data-testid="guest-email"
                            type="email"
                            value={guestForm.email}
                            onChange={(e) => setGuestForm({...guestForm, email: e.target.value})}
                            placeholder="guest@email.com (optional)"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-sm">Plus Ones Allowed</Label>
                          <Select
                            value={String(guestForm.plus_ones_allowed)}
                            onValueChange={(value) => setGuestForm({...guestForm, plus_ones_allowed: parseInt(value)})}
                          >
                            <SelectTrigger data-testid="guest-plus-ones">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0 - No plus ones</SelectItem>
                              <SelectItem value="1">1 - One guest</SelectItem>
                              <SelectItem value="2">2 - Two guests</SelectItem>
                              <SelectItem value="3">3 - Three guests</SelectItem>
                              <SelectItem value="4">4 - Four guests</SelectItem>
                              <SelectItem value="5">5 - Five guests</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <Label className="font-body text-sm">Plus One Names <span className="text-stone-400">(optional)</span></Label>
                            <button
                              type="button"
                              data-testid="add-plus-one-name-btn"
                              onClick={() => setGuestForm({...guestForm, plus_one_names: [...(guestForm.plus_one_names || []), '']})}
                              className="text-xs font-body text-[#B8A88A] hover:text-[#8A7A5C] flex items-center gap-1"
                            >
                              <Plus size={14} /> Add name
                            </button>
                          </div>
                          {(guestForm.plus_one_names || []).map((name, idx) => (
                            <div key={idx} className="flex gap-2">
                              <Input
                                data-testid={`plus-one-name-input-${idx}`}
                                value={name}
                                onChange={(e) => {
                                  const names = [...guestForm.plus_one_names];
                                  names[idx] = e.target.value;
                                  setGuestForm({...guestForm, plus_one_names: names});
                                }}
                                placeholder="Plus one full name"
                              />
                              <button
                                type="button"
                                onClick={() => setGuestForm({...guestForm, plus_one_names: guestForm.plus_one_names.filter((_, i) => i !== idx)})}
                                className="text-stone-400 hover:text-red-500"
                              >
                                <Trash size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={handleAddGuest}
                          disabled={isLoading}
                          data-testid="save-guest-btn"
                          className="w-full bg-[#C9B99A] hover:bg-[#B8A88A]"
                        >
                          {isLoading ? 'Saving...' : (editingGuest ? 'Update Guest' : 'Add Guest')}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                    </div>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Plus Ones</TableHead>
                      <TableHead>RSVP Status</TableHead>
                      <TableHead>Welcome Party</TableHead>
                      <TableHead>Beach Day</TableHead>
                      <TableHead>Set Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(!dashboardData.invited_guests || dashboardData.invited_guests.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-stone-500">
                          No guests added yet. Click "Add Guest" to start building your invite list.
                        </TableCell>
                      </TableRow>
                    ) : (
                      dashboardData.invited_guests.flatMap((guest) => {
                        // Get household members for this guest from addresses
                        const address = dashboardData.addresses?.find(
                          a => a.email?.toLowerCase() === guest.email?.toLowerCase() ||
                          (a.first_name?.toLowerCase() === guest.first_name?.toLowerCase() && 
                           a.last_name?.toLowerCase() === guest.last_name?.toLowerCase())
                        );
                        const householdMembers = address?.plus_ones || [];
                        
                        // Find RSVP for this guest (by guest_id first, then email)
                        const guestRsvp = dashboardData.rsvps?.find(r => r.guest_id === guest.id) ||
                          (guest.email ? dashboardData.rsvps?.find(
                            r => r.email?.toLowerCase() === guest.email.toLowerCase()
                          ) : undefined);
                        
                        // Merge in named plus-ones from the guest record and submitted RSVP plus-ones
                        const allMembers = [...householdMembers];
                        (guest.plus_one_names || []).forEach(n => {
                          if (n && !allMembers.some(m => m.name?.toLowerCase() === n.toLowerCase())) {
                            allMembers.push({ name: n });
                          }
                        });
                        (guestRsvp?.plus_ones || []).forEach(po => {
                          if (po.name && !allMembers.some(m => m.name?.toLowerCase() === po.name.toLowerCase())) {
                            allMembers.push({ name: po.name });
                          }
                        });
                        
                        const rows = [
                          // Main guest row
                          <TableRow key={guest.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-[#C9B99A] rounded-full flex items-center justify-center">
                                  <User size={12} className="text-white" />
                                </div>
                                {guest.first_name} {guest.last_name}
                              </div>
                            </TableCell>
                            <TableCell>{guest.email}</TableCell>
                            <TableCell>
                              <span className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded">
                                {guest.plus_ones_allowed} allowed
                              </span>
                            </TableCell>
                            <TableCell>
                              {guest.has_rsvped || guestRsvp ? (
                                guestRsvp?.attending ? (
                                  <span className="badge-attending">
                                    <Check size={14} className="mr-1" /> Attending
                                  </span>
                                ) : (
                                  <span className="badge-not-attending">
                                    <X size={14} className="mr-1" /> Declined
                                  </span>
                                )
                              ) : (
                                <span className="text-stone-400 text-sm">Awaiting</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <QuickEventStatus
                                testId={`guest-wp-${guest.id}`}
                                value={guestRsvp?.attending_welcome_party}
                                onYes={() => setGuestEventStatus(guest, 'attending_welcome_party', true)}
                                onNo={() => setGuestEventStatus(guest, 'attending_welcome_party', false)}
                              />
                            </TableCell>
                            <TableCell>
                              <QuickEventStatus
                                testId={`guest-bd-${guest.id}`}
                                value={guestRsvp?.attending_beach_day}
                                onYes={() => setGuestEventStatus(guest, 'attending_beach_day', true)}
                                onNo={() => setGuestEventStatus(guest, 'attending_beach_day', false)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.put(`${API}/admin/guest/${guest.id}/rsvp-status`, 
                                        { attending: true },
                                        { headers: { Authorization: `Bearer ${token}` }}
                                      );
                                      toast.success(`${guest.first_name} marked as attending`);
                                      fetchDashboardData();
                                    } catch (error) {
                                      toast.error('Failed to update status');
                                    }
                                  }}
                                  className={`px-2 py-1 text-xs rounded transition-colors ${
                                    guestRsvp?.attending 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-stone-100 text-stone-500 hover:bg-green-50 hover:text-green-600'
                                  }`}
                                  title="Mark as Attending"
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.put(`${API}/admin/guest/${guest.id}/rsvp-status`, 
                                        { attending: false },
                                        { headers: { Authorization: `Bearer ${token}` }}
                                      );
                                      toast.success(`${guest.first_name} marked as declined`);
                                      fetchDashboardData();
                                    } catch (error) {
                                      toast.error('Failed to update status');
                                    }
                                  }}
                                  className={`px-2 py-1 text-xs rounded transition-colors ${
                                    guestRsvp && !guestRsvp.attending 
                                      ? 'bg-red-100 text-red-700' 
                                      : 'bg-stone-100 text-stone-500 hover:bg-red-50 hover:text-red-600'
                                  }`}
                                  title="Mark as Declined"
                                >
                                  <X size={14} />
                                </button>
                                {(guest.has_rsvped || guestRsvp) && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        await axios.delete(`${API}/admin/guest/${guest.id}/rsvp-status`, 
                                          { headers: { Authorization: `Bearer ${token}` }}
                                        );
                                        toast.success(`${guest.first_name} reset to Awaiting`);
                                        fetchDashboardData();
                                      } catch (error) {
                                        toast.error('Failed to reset status');
                                      }
                                    }}
                                    className="px-2 py-1 text-xs rounded transition-colors bg-stone-100 text-stone-500 hover:bg-amber-50 hover:text-amber-600"
                                    title="Reset to Awaiting"
                                  >
                                    <ArrowCounterClockwise size={14} />
                                  </button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditGuest(guest)}
                                  className="text-stone-500 hover:text-[#B8A88A]"
                                >
                                  <PencilSimple size={18} />
                                </button>
                                <button
                                  onClick={() => handleDeleteGuest(guest.id)}
                                  className="text-stone-500 hover:text-red-500"
                                >
                                  <Trash size={18} />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>,
                          // Household member rows
                          ...allMembers.map((member, idx) => {
                            // Find if this household member has RSVP status
                            const memberRsvpStatus = guestRsvp?.plus_ones?.find(
                              po => po.name?.toLowerCase() === member.name?.toLowerCase()
                            );
                            
                            return (
                            <TableRow key={`${guest.id}-hh-${idx}`} className="bg-stone-50">
                              <TableCell className="font-medium pl-12">
                                <div className="flex items-center gap-2 text-stone-600">
                                  <div className="w-5 h-5 bg-stone-200 rounded-full flex items-center justify-center">
                                    <User size={10} className="text-stone-500" />
                                  </div>
                                  {member.name}
                                  <span className="text-xs text-stone-400">(household)</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-stone-500 text-sm">{member.email || '-'}</TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>
                                {memberRsvpStatus ? (
                                  memberRsvpStatus.attending ? (
                                    <span className="text-green-600 text-xs flex items-center gap-1">
                                      <Check size={12} /> Attending
                                    </span>
                                  ) : (
                                    <span className="text-red-500 text-xs flex items-center gap-1">
                                      <X size={12} /> Declined
                                    </span>
                                  )
                                ) : (
                                  <span className="text-stone-400 text-xs">With {guest.first_name}</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <QuickEventStatus
                                  testId={`member-wp-${guest.id}-${idx}`}
                                  value={memberRsvpStatus?.attending_welcome_party}
                                  onYes={() => setMemberEventStatus(guest, member.name, 'attending_welcome_party', true)}
                                  onNo={() => setMemberEventStatus(guest, member.name, 'attending_welcome_party', false)}
                                />
                              </TableCell>
                              <TableCell>
                                <QuickEventStatus
                                  testId={`member-bd-${guest.id}-${idx}`}
                                  value={memberRsvpStatus?.attending_beach_day}
                                  onYes={() => setMemberEventStatus(guest, member.name, 'attending_beach_day', true)}
                                  onNo={() => setMemberEventStatus(guest, member.name, 'attending_beach_day', false)}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <button
                                    onClick={async () => {
                                      try {
                                        await axios.put(`${API}/admin/household-member/rsvp-status`, 
                                          { 
                                            member_name: member.name,
                                            member_email: member.email || '',
                                            main_guest_id: guest.id,
                                            attending: true 
                                          },
                                          { headers: { Authorization: `Bearer ${token}` }}
                                        );
                                        toast.success(`${member.name} marked as attending`);
                                        fetchDashboardData();
                                      } catch (error) {
                                        toast.error('Failed to update status');
                                      }
                                    }}
                                    className={`px-2 py-1 text-xs rounded transition-colors ${
                                      memberRsvpStatus?.attending 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-stone-100 text-stone-500 hover:bg-green-50 hover:text-green-600'
                                    }`}
                                    title="Mark as Attending"
                                  >
                                    <Check size={12} />
                                  </button>
                                  <button
                                    onClick={async () => {
                                      try {
                                        await axios.put(`${API}/admin/household-member/rsvp-status`, 
                                          { 
                                            member_name: member.name,
                                            member_email: member.email || '',
                                            main_guest_id: guest.id,
                                            attending: false 
                                          },
                                          { headers: { Authorization: `Bearer ${token}` }}
                                        );
                                        toast.success(`${member.name} marked as declined`);
                                        fetchDashboardData();
                                      } catch (error) {
                                        toast.error('Failed to update status');
                                      }
                                    }}
                                    className={`px-2 py-1 text-xs rounded transition-colors ${
                                      memberRsvpStatus && !memberRsvpStatus.attending 
                                        ? 'bg-red-100 text-red-700' 
                                        : 'bg-stone-100 text-stone-500 hover:bg-red-50 hover:text-red-600'
                                    }`}
                                    title="Mark as Declined"
                                  >
                                    <X size={12} />
                                  </button>
                                  {memberRsvpStatus && (
                                    <button
                                      onClick={async () => {
                                        try {
                                          await axios.put(`${API}/admin/household-member/reset-status`, 
                                            { 
                                              member_name: member.name,
                                              main_guest_id: guest.id
                                            },
                                            { headers: { Authorization: `Bearer ${token}` }}
                                          );
                                          toast.success(`${member.name} reset to Awaiting`);
                                          fetchDashboardData();
                                        } catch (error) {
                                          toast.error('Failed to reset status');
                                        }
                                      }}
                                      className="px-2 py-1 text-xs rounded transition-colors bg-stone-100 text-stone-500 hover:bg-amber-50 hover:text-amber-600"
                                      title="Reset to Awaiting"
                                    >
                                      <ArrowCounterClockwise size={12} />
                                    </button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>-</TableCell>
                            </TableRow>
                          )})
                        ];
                        return rows;
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* RSVPs Tab */}
            <TabsContent value="rsvps">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200 flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className="font-display text-lg">RSVPs</h3>
                    <p className="font-body text-sm text-stone-500">Guest responses and preferences</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="welcome-party-list-btn"
                      variant="outline"
                      className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                      onClick={() => {
                        const attendees = [];
                        (dashboardData?.rsvps || []).forEach(r => {
                          if (!r.attending) return;
                          if (r.attending_welcome_party === true) {
                            attendees.push({ name: `${r.first_name} ${r.last_name}`, household: `${r.first_name} ${r.last_name}`, email: r.email || '', dietary: r.dietary_restrictions || '', type: 'Primary' });
                          }
                          (r.plus_ones || []).forEach(p => {
                            if (p.attending !== false && p.attending_welcome_party === true) {
                              attendees.push({ name: p.name, household: `${r.first_name} ${r.last_name}`, email: r.email || '', dietary: p.dietary_restrictions || '', type: 'Plus One' });
                            }
                          });
                        });
                        if (attendees.length === 0) {
                          toast.error('No guests have said yes to the Welcome Party yet');
                          return;
                        }
                        const csv = [
                          '#,guest_name,household_contact,contact_email,dietary_restrictions,type',
                          ...attendees.map((p, i) => [i + 1, `"${p.name}"`, `"${p.household}"`, p.email, `"${p.dietary.replace(/"/g, '""')}"`, p.type].join(','))
                        ].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'welcome_party_list.csv';
                        a.click();
                        window.URL.revokeObjectURL(url);
                        toast.success(`Welcome Party list exported — ${attendees.length} guest${attendees.length > 1 ? 's' : ''} attending!`);
                      }}
                    >
                      <Heart size={18} className="mr-2" />
                      Welcome Party List
                    </Button>
                    <Button
                      data-testid="beach-bus-list-btn"
                      variant="outline"
                      className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                      onClick={() => {
                        const riders = [];
                        (dashboardData?.rsvps || []).forEach(r => {
                          if (!r.attending) return;
                          if (r.attending_beach_day === true) {
                            riders.push({ name: `${r.first_name} ${r.last_name}`, household: `${r.first_name} ${r.last_name}`, email: r.email || '', type: 'Primary' });
                          }
                          (r.plus_ones || []).forEach(p => {
                            if (p.attending !== false && p.attending_beach_day === true) {
                              riders.push({ name: p.name, household: `${r.first_name} ${r.last_name}`, email: r.email || '', type: 'Plus One' });
                            }
                          });
                        });
                        if (riders.length === 0) {
                          toast.error('No guests have said yes to the Beach Day yet');
                          return;
                        }
                        const csv = [
                          '#,guest_name,household_contact,contact_email,type',
                          ...riders.map((p, i) => [i + 1, `"${p.name}"`, `"${p.household}"`, p.email, p.type].join(','))
                        ].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'beach_day_bus_list.csv';
                        a.click();
                        window.URL.revokeObjectURL(url);
                        toast.success(`Bus list exported — ${riders.length} guest${riders.length > 1 ? 's' : ''} riding to El Tunco!`);
                      }}
                    >
                      <Bus size={18} className="mr-2" />
                      Beach Day Bus List
                    </Button>
                    <Button
                      data-testid="email-guests-btn-rsvps"
                      variant="outline"
                      className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                      onClick={() => openEmailDialog('not_rsvped')}
                    >
                      <Envelope size={18} className="mr-2" />
                      Email Guests
                    </Button>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".csv"
                        id="rsvp-csv-upload"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          try {
                            const response = await axios.post(`${API}/admin/rsvps/upload-csv`, formData, {
                              headers: { 
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data'
                              },
                            });
                            toast.success(`Imported ${response.data.imported} RSVPs`);
                            if (response.data.total_errors > 0) {
                              toast.warning(`${response.data.total_errors} rows had errors`);
                            }
                            fetchDashboardData();
                          } catch (error) {
                            toast.error('Failed to import CSV');
                            console.error(error);
                          }
                          e.target.value = '';
                        }}
                      />
                      <Button
                        variant="outline"
                        className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                        onClick={() => document.getElementById('rsvp-csv-upload').click()}
                      >
                        <UploadSimple size={18} className="mr-2" />
                        Import CSV
                      </Button>
                    </div>
                    <Button
                      onClick={() => {
                        if (!dashboardData?.rsvps?.length) {
                          toast.error('No RSVPs to export');
                          return;
                        }
                        const headers = ['first_name', 'last_name', 'email', 'attending', 'welcome_party', 'beach_day', 'meal_choice', 'dietary_restrictions', 'song_request', 'message', 'plus_ones_names', 'plus_ones_welcome_party', 'plus_ones_beach_day', 'plus_ones_meals', 'plus_ones_dietary', 'plus_ones_songs', 'created_at'];
                        const csv = [
                          headers.join(','),
                          ...dashboardData.rsvps.map(r => [
                            r.first_name || '',
                            r.last_name || '',
                            r.email || '',
                            r.attending ? 'Yes' : 'No',
                            r.attending_welcome_party === true ? 'Yes' : r.attending_welcome_party === false ? 'No' : 'Not answered',
                            r.attending_beach_day === true ? 'Yes' : r.attending_beach_day === false ? 'No' : 'Not answered',
                            r.meal_choice || '',
                            `"${(r.dietary_restrictions || '').replace(/"/g, '""')}"`,
                            `"${(r.song_request || '').replace(/"/g, '""')}"`,
                            `"${(r.message || '').replace(/"/g, '""')}"`,
                            `"${(r.plus_ones || []).map(p => p.name).join('; ')}"`,
                            `"${(r.plus_ones || []).map(p => p.attending_welcome_party === true ? 'Yes' : p.attending_welcome_party === false ? 'No' : 'Not answered').join('; ')}"`,
                            `"${(r.plus_ones || []).map(p => p.attending_beach_day === true ? 'Yes' : p.attending_beach_day === false ? 'No' : 'Not answered').join('; ')}"`,
                            `"${(r.plus_ones || []).map(p => p.meal_choice || '').join('; ')}"`,
                            `"${(r.plus_ones || []).map(p => p.dietary_restrictions || '').join('; ')}"`,
                            `"${(r.plus_ones || []).map(p => p.song_request || '').join('; ')}"`,
                            r.created_at || ''
                          ].join(','))
                        ].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'rsvps.csv';
                        a.click();
                        window.URL.revokeObjectURL(url);
                        toast.success('RSVPs exported!');
                      }}
                      variant="outline"
                      className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                    >
                      <DownloadSimple size={18} className="mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
                
                {/* CSV Format Help */}
                <div className="px-4 py-2 bg-stone-50 border-b border-stone-200">
                  <p className="font-body text-xs text-stone-500">
                    <strong>CSV Format:</strong> first_name, last_name, email, attending (Yes/No), meal_choice, dietary_restrictions, song_request, message, plus_ones_names (comma or semicolon separated)
                  </p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Welcome Party</TableHead>
                      <TableHead>Beach Day</TableHead>
                      <TableHead>Meal</TableHead>
                      <TableHead>Dietary</TableHead>
                      <TableHead>Song Request</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Plus Ones</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboardData.rsvps.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center py-8 text-stone-500">
                          No RSVPs yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      dashboardData.rsvps.map((rsvp) => (
                        <TableRow key={rsvp.id}>
                          <TableCell className="font-medium">
                            {rsvp.first_name} {rsvp.last_name}
                          </TableCell>
                          <TableCell>{rsvp.email}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 items-start">
                              <button
                                data-testid={`rsvp-set-attending-${rsvp.id}`}
                                onClick={() => !rsvp.attending && setRsvpAttending(rsvp, true)}
                                title="Set to Attending"
                                className={rsvp.attending
                                  ? 'badge-attending cursor-default'
                                  : 'inline-flex items-center text-xs px-2 py-1 rounded bg-stone-100 text-stone-400 hover:bg-green-50 hover:text-green-600 transition-colors'}
                              >
                                <Check size={14} className="mr-1" /> Attending
                              </button>
                              <button
                                data-testid={`rsvp-set-declined-${rsvp.id}`}
                                onClick={() => rsvp.attending && setRsvpAttending(rsvp, false)}
                                title="Set to Declined"
                                className={!rsvp.attending
                                  ? 'badge-not-attending cursor-default'
                                  : 'inline-flex items-center text-xs px-2 py-1 rounded bg-stone-100 text-stone-400 hover:bg-red-50 hover:text-red-600 transition-colors'}
                              >
                                <X size={14} className="mr-1" /> Declined
                              </button>
                            </div>
                          </TableCell>
                          <TableCell data-testid={`rsvp-welcome-party-${rsvp.id}`}>
                            {rsvp.attending_welcome_party === true ? (
                              <span className="badge-attending">
                                <Check size={14} className="mr-1" /> Yes
                              </span>
                            ) : rsvp.attending_welcome_party === false ? (
                              <span className="badge-not-attending">
                                <X size={14} className="mr-1" /> No
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-1 bg-stone-100 text-stone-500 rounded whitespace-nowrap">Not answered</span>
                            )}
                          </TableCell>
                          <TableCell data-testid={`rsvp-beach-day-${rsvp.id}`}>
                            {rsvp.attending_beach_day === true ? (
                              <span className="badge-attending">
                                <Check size={14} className="mr-1" /> Yes
                              </span>
                            ) : rsvp.attending_beach_day === false ? (
                              <span className="badge-not-attending">
                                <X size={14} className="mr-1" /> No
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-1 bg-stone-100 text-stone-500 rounded whitespace-nowrap">Not answered</span>
                            )}
                          </TableCell>
                          <TableCell className="capitalize">{rsvp.meal_choice || '-'}</TableCell>
                          <TableCell>{rsvp.dietary_restrictions || '-'}</TableCell>
                          <TableCell>{rsvp.song_request || '-'}</TableCell>
                          <TableCell className="max-w-[200px]">
                            {rsvp.message ? (
                              <p className="text-sm text-stone-600 truncate" title={rsvp.message}>
                                {rsvp.message}
                              </p>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {rsvp.plus_ones && rsvp.plus_ones.length > 0 ? (
                              <div className="space-y-2">
                                {rsvp.plus_ones.map((po, i) => (
                                  <div key={i} className="text-sm border-l-2 border-stone-200 pl-2">
                                    <div className="flex items-center gap-1">
                                      <span className="font-medium">{po.name}</span>
                                      {po.attending === false && <span className="text-red-500 text-xs">(Declined)</span>}
                                    </div>
                                    {po.attending !== false && (
                                      <div className="text-xs text-stone-500 space-y-0.5">
                                        <div>Welcome Party: {po.attending_welcome_party === true ? 'Yes' : po.attending_welcome_party === false ? 'No' : 'Not answered'}</div>
                                        <div>Beach Day: {po.attending_beach_day === true ? 'Yes' : po.attending_beach_day === false ? 'No' : 'Not answered'}</div>
                                        {po.meal_choice && <div>Meal: {po.meal_choice}</div>}
                                        {po.dietary_restrictions && <div>Dietary: {po.dietary_restrictions}</div>}
                                        {po.song_request && <div>Song: {po.song_request}</div>}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-stone-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-stone-500 whitespace-nowrap">
                              {formatTimestamp(rsvp.created_at)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setEditingRsvp(rsvp);
                                  setRsvpForm({
                                    first_name: rsvp.first_name || '',
                                    last_name: rsvp.last_name || '',
                                    email: rsvp.email || '',
                                    attending: rsvp.attending ?? true,
                                    attending_welcome_party: rsvp.attending_welcome_party ?? null,
                                    attending_beach_day: rsvp.attending_beach_day ?? null,
                                    meal_choice: rsvp.meal_choice || '',
                                    dietary_restrictions: rsvp.dietary_restrictions || '',
                                    song_request: rsvp.song_request || '',
                                    message: rsvp.message || '',
                                    plus_ones: rsvp.plus_ones || []
                                  });
                                  setShowEditRsvp(true);
                                }}
                                className="text-stone-500 hover:text-[#B8A88A]"
                                title="Edit RSVP"
                              >
                                <PencilSimple size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteRSVP(rsvp.id)}
                                className="text-stone-500 hover:text-red-500"
                                title="Delete RSVP"
                              >
                                <Trash size={18} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Flights Tab */}
            <TabsContent value="flights">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200 flex justify-between items-center">
                  <div>
                    <h3 className="font-display text-lg">Flights & Accommodations</h3>
                    <p className="font-body text-sm text-stone-500">Guest travel and stay details for coordination</p>
                  </div>
                  <Button
                    onClick={() => {
                      if (!dashboardData?.flights?.length) {
                        toast.error('No travel info to export');
                        return;
                      }
                      const headers = ['guest_name', 'arrival_date', 'arrival_time', 'arrival_airline', 'arrival_flight', 'departure_date', 'departure_time', 'departure_airline', 'departure_flight', 'accommodation_name', 'checkin', 'checkout', 'needs_transportation', 'notes', 'accommodation_notes', 'submitted_at'];
                      const csv = [
                        headers.join(','),
                        ...dashboardData.flights.map(f => [
                          `"${f.guest_name || ''}"`,
                          `"${f.arrival_date || ''}"`,
                          `"${f.arrival_time || ''}"`,
                          `"${f.arrival_airline || ''}"`,
                          `"${f.arrival_flight || ''}"`,
                          `"${f.departure_date || ''}"`,
                          `"${f.departure_time || ''}"`,
                          `"${f.departure_airline || ''}"`,
                          `"${f.departure_flight || ''}"`,
                          `"${f.accommodation_name || ''}"`,
                          `"${f.accommodation_checkin || ''}"`,
                          `"${f.accommodation_checkout || ''}"`,
                          `"${f.needs_transportation ? 'Yes' : 'No'}"`,
                          `"${(f.notes || '').replace(/"/g, '""')}"`,
                          `"${(f.accommodation_notes || '').replace(/"/g, '""')}"`,
                          `"${f.created_at || ''}"`
                        ].join(','))
                      ].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'guest_travel_info.csv';
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Travel info exported!');
                    }}
                    variant="outline"
                    className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                  >
                    <DownloadSimple size={18} className="mr-2" />
                    Export CSV
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guest</TableHead>
                      <TableHead>Arrival</TableHead>
                      <TableHead>Departure</TableHead>
                      <TableHead>Accommodation</TableHead>
                      <TableHead>Transportation</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboardData.flights.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-stone-500">
                          No travel info submitted yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      dashboardData.flights.map((flight) => (
                        <TableRow key={flight.id}>
                          <TableCell className="font-medium">{flight.guest_name}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>{flight.arrival_date} {flight.arrival_time}</p>
                              {(flight.arrival_airline || flight.arrival_flight) && (
                                <p className="text-stone-500 text-xs">{flight.arrival_airline} {flight.arrival_flight}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>{flight.departure_date} {flight.departure_time}</p>
                              {(flight.departure_airline || flight.departure_flight) && (
                                <p className="text-stone-500 text-xs">{flight.departure_airline} {flight.departure_flight}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {flight.accommodation_name ? (
                                <>
                                  <p className="font-medium text-[#B8A88A]">{flight.accommodation_name}</p>
                                  {(flight.accommodation_checkin || flight.accommodation_checkout) && (
                                    <p className="text-stone-500 text-xs">
                                      {flight.accommodation_checkin} → {flight.accommodation_checkout}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <span className="text-stone-400">Not specified</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {flight.needs_transportation ? (
                              <span className="text-[#B8A88A]">Requested</span>
                            ) : (
                              <span className="text-stone-400">Not needed</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-stone-500 whitespace-nowrap">
                              {formatTimestamp(flight.created_at)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {flight.notes && <p>{flight.notes}</p>}
                              {flight.accommodation_notes && (
                                <p className="text-stone-400 text-xs mt-1">Stay: {flight.accommodation_notes}</p>
                              )}
                              {!flight.notes && !flight.accommodation_notes && '-'}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Meals Tab */}
            <TabsContent value="meals">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200 flex justify-between items-center">
                  <div>
                    <h3 className="font-display text-lg">Meal Preferences</h3>
                    <p className="font-body text-sm text-stone-500">Guest meal selections from RSVPs</p>
                  </div>
                  <Button
                    onClick={() => {
                      // Combine main guest meals and plus-one meals
                      const allMeals = [];
                      dashboardData.rsvps.forEach(rsvp => {
                        if (rsvp.attending && rsvp.meal_choice) {
                          allMeals.push({
                            guest_name: `${rsvp.first_name} ${rsvp.last_name}`,
                            meal_choice: rsvp.meal_choice,
                            dietary_restrictions: rsvp.dietary_restrictions || '',
                            is_plus_one: false
                          });
                        }
                        if (rsvp.plus_ones) {
                          rsvp.plus_ones.forEach(po => {
                            if (po.attending !== false && po.meal_choice) {
                              allMeals.push({
                                guest_name: po.name,
                                meal_choice: po.meal_choice,
                                dietary_restrictions: po.dietary_restrictions || '',
                                is_plus_one: true
                              });
                            }
                          });
                        }
                      });
                      if (allMeals.length === 0) {
                        toast.error('No meal preferences to export');
                        return;
                      }
                      const headers = ['guest_name', 'meal_choice', 'dietary_restrictions', 'is_plus_one'];
                      const csv = [
                        headers.join(','),
                        ...allMeals.map(m => [
                          `"${m.guest_name}"`,
                          m.meal_choice,
                          `"${m.dietary_restrictions.replace(/"/g, '""')}"`,
                          m.is_plus_one ? 'Yes' : 'No'
                        ].join(','))
                      ].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'meal_preferences.csv';
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Meal preferences exported!');
                    }}
                    variant="outline"
                    className="border-[#C9B99A] text-[#B8A88A] hover:bg-[#F0EBE3]"
                  >
                    <DownloadSimple size={18} className="mr-2" />
                    Export CSV
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guest</TableHead>
                      <TableHead>Meal Choice</TableHead>
                      <TableHead>Dietary Restrictions</TableHead>
                      <TableHead>Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const allMeals = [];
                      dashboardData.rsvps.forEach(rsvp => {
                        if (rsvp.attending && rsvp.meal_choice) {
                          allMeals.push({
                            id: rsvp.id,
                            guest_name: `${rsvp.first_name} ${rsvp.last_name}`,
                            meal_choice: rsvp.meal_choice,
                            dietary_restrictions: rsvp.dietary_restrictions || '',
                            is_plus_one: false
                          });
                        }
                        if (rsvp.plus_ones) {
                          rsvp.plus_ones.forEach((po, idx) => {
                            if (po.attending !== false && po.meal_choice) {
                              allMeals.push({
                                id: `${rsvp.id}-po-${idx}`,
                                guest_name: po.name,
                                meal_choice: po.meal_choice,
                                dietary_restrictions: po.dietary_restrictions || '',
                                is_plus_one: true
                              });
                            }
                          });
                        }
                      });
                      if (allMeals.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-8 text-stone-500">
                              No meal preferences submitted yet
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return allMeals.map((meal) => (
                        <TableRow key={meal.id}>
                          <TableCell className="font-medium">{meal.guest_name}</TableCell>
                          <TableCell className="capitalize">{meal.meal_choice}</TableCell>
                          <TableCell>{meal.dietary_restrictions || '-'}</TableCell>
                          <TableCell>
                            {meal.is_plus_one ? (
                              <span className="text-xs px-2 py-1 bg-stone-100 text-stone-600 rounded">Plus One</span>
                            ) : (
                              <span className="text-xs px-2 py-1 bg-[#F0EBE3] text-[#B8A88A] rounded">Primary</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Bridal Party Edit Tab */}
            <TabsContent value="bridal-edit">
              <div className="bg-white border border-stone-200 p-6">
                <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                  <div>
                    <h3 className="font-display text-lg">Bridal Party Page</h3>
                    <p className="font-body text-sm text-stone-500">Edit the bride, groom, bridesmaids and groomsmen shown on the Bridal Party page</p>
                  </div>
                  <Button
                    data-testid="save-bridal-btn"
                    onClick={() => saveContent('bridal_party', bridalContent)}
                    disabled={contentLoading || !bridalContent}
                    className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                  >
                    {contentLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>

                {bridalContent ? (
                  <div className="space-y-8">
                    {/* Page subtitle */}
                    <div className="space-y-2">
                      <Label className="font-body text-sm">Page Subtitle</Label>
                      <Input
                        data-testid="bridal-subtitle"
                        value={bridalContent.subtitle || ''}
                        onChange={(e) => setBridalContent({ ...bridalContent, subtitle: e.target.value })}
                        className="bg-white border-stone-200"
                      />
                    </div>

                    {/* Bride & Groom */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {['bride', 'groom'].map((personKey) => {
                        const person = bridalContent[personKey] || {};
                        const setPerson = (field, value) => setBridalContent({ ...bridalContent, [personKey]: { ...person, [field]: value } });
                        return (
                          <div key={personKey} className="border-2 border-[#E8E0D0] p-4 space-y-3 bg-[#FAF8F5]">
                            <h4 className="font-display text-base capitalize">{personKey}</h4>
                            <div className="flex items-center gap-4">
                              <div className="w-20 h-20 rounded-full overflow-hidden bg-stone-200 flex-shrink-0 flex items-center justify-center">
                                {person.image ? (
                                  <img src={person.image} alt={person.name} className="w-full h-full object-cover" style={{ objectPosition: person.image_position || '50% 50%' }} />
                                ) : (
                                  <User size={28} className="text-stone-400" />
                                )}
                              </div>
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  data-testid={`bridal-${personKey}-photo-input`}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                      const res = await axios.post(`${API}/admin/upload-image`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
                                      setPerson('image', res.data.url);
                                      toast.success('Photo uploaded!');
                                    } catch (err) {
                                      toast.error('Upload failed');
                                    }
                                    e.target.value = '';
                                  }}
                                />
                                <Button type="button" variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A] whitespace-nowrap" asChild>
                                  <span><UploadSimple size={14} className="mr-1" />Upload Photo</span>
                                </Button>
                              </label>
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs text-stone-500">Name</Label>
                              <Input
                                data-testid={`bridal-${personKey}-name`}
                                value={person.name || ''}
                                onChange={(e) => setPerson('name', e.target.value)}
                                className="bg-white border-stone-200"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs text-stone-500">Title (e.g., The Bride)</Label>
                              <Input
                                value={person.title || ''}
                                onChange={(e) => setPerson('title', e.target.value)}
                                className="bg-white border-stone-200"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs text-stone-500">Description</Label>
                              <Textarea
                                value={person.description || ''}
                                onChange={(e) => setPerson('description', e.target.value)}
                                className="bg-white border-stone-200 min-h-[70px]"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs text-stone-500">Photo URL (or use Upload above)</Label>
                              <Input
                                value={person.image || ''}
                                onChange={(e) => setPerson('image', e.target.value)}
                                className="bg-white border-stone-200 text-xs"
                                placeholder="https://..."
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Bridesmaids & Groomsmen lists */}
                    {[
                      { listKey: 'bridesmaids', label: 'Bridesmaids', titleKey: 'bridesmaids_title', subtitleKey: 'bridesmaids_subtitle', defaultRole: 'Bridesmaid' },
                      { listKey: 'groomsmen', label: 'Groomsmen', titleKey: 'groomsmen_title', subtitleKey: 'groomsmen_subtitle', defaultRole: 'Groomsman' }
                    ].map(({ listKey, label, titleKey, subtitleKey, defaultRole }) => {
                      const members = bridalContent[listKey] || [];
                      const setMembers = (newMembers) => setBridalContent({ ...bridalContent, [listKey]: newMembers });
                      return (
                        <div key={listKey} className="border-t border-stone-200 pt-6 space-y-4">
                          <div className="flex justify-between items-center flex-wrap gap-2">
                            <h4 className="font-display text-base">{label}</h4>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              data-testid={`bridal-add-${listKey}-btn`}
                              onClick={() => setMembers([...members, { name: '', role: defaultRole, relationship: '', description: '', image: '', image_position: '50% 50%' }])}
                              className="text-[#B8A88A] border-[#C9B99A] hover:bg-[#F0EBE3]"
                            >
                              <Plus size={16} className="mr-1" /> Add {defaultRole}
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label className="font-body text-xs text-stone-500">Section Title</Label>
                              <Input
                                value={bridalContent[titleKey] || ''}
                                onChange={(e) => setBridalContent({ ...bridalContent, [titleKey]: e.target.value })}
                                className="bg-white border-stone-200"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs text-stone-500">Section Subtitle</Label>
                              <Input
                                value={bridalContent[subtitleKey] || ''}
                                onChange={(e) => setBridalContent({ ...bridalContent, [subtitleKey]: e.target.value })}
                                className="bg-white border-stone-200"
                              />
                            </div>
                          </div>

                          {members.length === 0 && (
                            <p className="font-body text-sm text-stone-400 italic">No {label.toLowerCase()} added yet — click "Add {defaultRole}"</p>
                          )}

                          {members.map((member, index) => {
                            const updateMember = (field, value) => {
                              const newMembers = [...members];
                              newMembers[index] = { ...newMembers[index], [field]: value };
                              setMembers(newMembers);
                            };
                            return (
                              <div key={index} className="bg-stone-50 border border-stone-200 p-4 space-y-3" data-testid={`bridal-${listKey}-${index}`}>
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-3">
                                    <div className="w-14 h-14 rounded-full overflow-hidden bg-stone-200 flex-shrink-0 flex items-center justify-center">
                                      {member.image ? (
                                        <img src={member.image} alt={member.name} className="w-full h-full object-cover" style={{ objectPosition: member.image_position || '50% 50%' }} />
                                      ) : (
                                        <User size={22} className="text-stone-400" />
                                      )}
                                    </div>
                                    <span className="font-body text-sm font-medium text-stone-700">{member.name || `${defaultRole} ${index + 1}`}</span>
                                  </div>
                                  <div className="flex gap-1 items-center">
                                    <button
                                      type="button"
                                      disabled={index === 0}
                                      onClick={() => {
                                        const newMembers = [...members];
                                        [newMembers[index - 1], newMembers[index]] = [newMembers[index], newMembers[index - 1]];
                                        setMembers(newMembers);
                                      }}
                                      className="p-1 text-stone-400 hover:text-[#B8A88A] disabled:opacity-30"
                                      title="Move up"
                                    >
                                      <ArrowUp size={16} />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={index === members.length - 1}
                                      onClick={() => {
                                        const newMembers = [...members];
                                        [newMembers[index], newMembers[index + 1]] = [newMembers[index + 1], newMembers[index]];
                                        setMembers(newMembers);
                                      }}
                                      className="p-1 text-stone-400 hover:text-[#B8A88A] disabled:opacity-30"
                                      title="Move down"
                                    >
                                      <ArrowDown size={16} />
                                    </button>
                                    <button
                                      type="button"
                                      data-testid={`bridal-${listKey}-${index}-remove`}
                                      onClick={() => setMembers(members.filter((_, i) => i !== index))}
                                      className="p-1 text-stone-400 hover:text-red-500"
                                      title="Remove"
                                    >
                                      <Trash size={16} />
                                    </button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div className="space-y-1">
                                    <Label className="font-body text-xs text-stone-500">Name</Label>
                                    <Input
                                      data-testid={`bridal-${listKey}-${index}-name`}
                                      value={member.name || ''}
                                      onChange={(e) => updateMember('name', e.target.value)}
                                      className="bg-white border-stone-200"
                                      placeholder="Full name"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="font-body text-xs text-stone-500">Role</Label>
                                    <Input
                                      value={member.role || ''}
                                      onChange={(e) => updateMember('role', e.target.value)}
                                      className="bg-white border-stone-200"
                                      placeholder={defaultRole}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="font-body text-xs text-stone-500">Relationship</Label>
                                    <Input
                                      value={member.relationship || ''}
                                      onChange={(e) => updateMember('relationship', e.target.value)}
                                      className="bg-white border-stone-200"
                                      placeholder="e.g., Bride's Sister"
                                    />
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <Label className="font-body text-xs text-stone-500">Description</Label>
                                  <Textarea
                                    value={member.description || ''}
                                    onChange={(e) => updateMember('description', e.target.value)}
                                    className="bg-white border-stone-200 min-h-[60px]"
                                    placeholder="A short note about this person"
                                  />
                                </div>
                                <div className="flex items-end gap-3 flex-wrap">
                                  <div className="space-y-1 flex-1 min-w-[200px]">
                                    <Label className="font-body text-xs text-stone-500">Photo URL (or use Upload)</Label>
                                    <Input
                                      value={member.image || ''}
                                      onChange={(e) => updateMember('image', e.target.value)}
                                      className="bg-white border-stone-200 text-xs"
                                      placeholder="https://..."
                                    />
                                  </div>
                                  <label className="cursor-pointer">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      data-testid={`bridal-${listKey}-${index}-photo-input`}
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const formData = new FormData();
                                        formData.append('file', file);
                                        try {
                                          const res = await axios.post(`${API}/admin/upload-image`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
                                          updateMember('image', res.data.url);
                                          toast.success('Photo uploaded!');
                                        } catch (err) {
                                          toast.error('Upload failed');
                                        }
                                        e.target.value = '';
                                      }}
                                    />
                                    <Button type="button" variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A] whitespace-nowrap" asChild>
                                      <span><UploadSimple size={14} className="mr-1" />Upload Photo</span>
                                    </Button>
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}

                    {/* Bottom note */}
                    <div className="border-t border-stone-200 pt-6 space-y-2">
                      <Label className="font-body text-sm">Bottom Note (leave empty to hide)</Label>
                      <Textarea
                        data-testid="bridal-note"
                        value={bridalContent.coming_soon_note || ''}
                        onChange={(e) => setBridalContent({ ...bridalContent, coming_soon_note: e.target.value })}
                        className="bg-white border-stone-200 min-h-[60px]"
                        placeholder="e.g., Photos and personal messages coming soon!"
                      />
                    </div>

                    <div className="flex justify-end pt-2">
                      <Button
                        data-testid="save-bridal-btn-bottom"
                        onClick={() => saveContent('bridal_party', bridalContent)}
                        disabled={contentLoading}
                        className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                      >
                        {contentLoading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="font-body text-stone-500">Loading content...</p>
                )}
              </div>
            </TabsContent>

            {/* Stats Tab */}
            <TabsContent value="stats">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-stone-200 p-6">
                  <h3 className="font-display text-lg text-stone-900 mb-4">RSVP Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-stone-600">Total Invited</span>
                      <span className="font-body font-medium">{dashboardData.summary.total_invited}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-stone-600">Responded</span>
                      <span className="font-body font-medium">{dashboardData.summary.total_rsvps}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-stone-600">Awaiting Response</span>
                      <span className="font-body font-medium text-[#B8A88A]">{dashboardData.summary.awaiting_rsvp}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-stone-600">Households Attending</span>
                      <span className="font-body font-medium text-[#B8A88A]">{dashboardData.summary.attending}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-stone-600">Households Declined</span>
                      <span className="font-body font-medium text-stone-400">{dashboardData.summary.not_attending}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-stone-600">Plus Ones Attending</span>
                      <span className="font-body font-medium">{dashboardData.summary.plus_ones}</span>
                    </div>
                    <div className="border-t border-stone-200 pt-3 mt-3 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-body font-medium text-stone-900">Wedding — Total Guests</span>
                        <span className="font-display text-2xl text-[#B8A88A]" data-testid="stats-wedding-total">{dashboardData.summary.wedding_guests}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-body font-medium text-stone-900">Welcome Party (Dec 10) — Total Guests</span>
                        <span className="font-display text-2xl text-[#B8A88A]" data-testid="stats-welcome-party-total">{dashboardData.summary.welcome_party_guests}</span>
                      </div>
                      {dashboardData.summary.welcome_party_not_answered > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="font-body text-sm text-amber-600">Welcome Party — Not Answered</span>
                          <span className="font-body font-medium text-amber-600">{dashboardData.summary.welcome_party_not_answered}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="font-body font-medium text-stone-900">Beach Day (Dec 12) — Total Guests</span>
                        <span className="font-display text-2xl text-[#B8A88A]" data-testid="stats-beach-day-total">{dashboardData.summary.beach_day_guests}</span>
                      </div>
                      {dashboardData.summary.beach_day_not_answered > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="font-body text-sm text-amber-600">Beach Day — Not Answered</span>
                          <span className="font-body font-medium text-amber-600">{dashboardData.summary.beach_day_not_answered}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-stone-200 p-6">
                  <h3 className="font-display text-lg text-stone-900 mb-4">Meal Counts</h3>
                  {Object.keys(dashboardData.meal_counts).length === 0 ? (
                    <p className="font-body text-stone-500">No meal preferences yet</p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(dashboardData.meal_counts).map(([meal, count]) => (
                        <div key={meal} className="flex justify-between items-center">
                          <span className="font-body text-stone-600 capitalize">{meal}</span>
                          <span className="font-body font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Slideshow Tab */}
            <TabsContent value="slideshow">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200 flex justify-between items-center">
                  <div>
                    <h3 className="font-display text-lg">Homepage Slideshow</h3>
                    <p className="font-body text-sm text-stone-500">
                      Manage the images in your homepage hero slideshow
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingImage(null);
                      setImageForm({ url: '', position: 'center center', scale: 'cover' });
                      setShowAddImage(true);
                    }}
                    className="bg-[#C9B99A] hover:bg-[#B8A88A]"
                  >
                    <Plus size={18} className="mr-2" />
                    Add Image
                  </Button>
                </div>

                <div className="p-4">
                  {slideshowImages.length === 0 ? (
                    <p className="text-center text-stone-500 py-8">
                      No slideshow images configured yet. Add your first image above.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {slideshowImages.map((image, index) => (
                        <div key={image.id} className="flex items-center gap-4 p-4 border border-stone-200 bg-stone-50">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => moveImage(index, 'up')}
                              disabled={index === 0}
                              className="p-1 text-stone-400 hover:text-stone-600 disabled:opacity-30"
                            >
                              <ArrowUp size={16} />
                            </button>
                            <span className="text-xs text-stone-500 text-center">{index + 1}</span>
                            <button
                              onClick={() => moveImage(index, 'down')}
                              disabled={index === slideshowImages.length - 1}
                              className="p-1 text-stone-400 hover:text-stone-600 disabled:opacity-30"
                            >
                              <ArrowDown size={16} />
                            </button>
                          </div>
                          
                          <div 
                            className="w-32 h-20 bg-stone-200 flex-shrink-0"
                            style={{
                              backgroundImage: `url(${image.url})`,
                              backgroundSize: image.scale || 'cover',
                              backgroundPosition: image.position || 'center center',
                              backgroundRepeat: 'no-repeat'
                            }}
                          />
                          
                          <div className="flex-1 min-w-0">
                            <p className="font-body text-sm text-stone-700 truncate">
                              {image.url.split('/').pop()}
                            </p>
                            <p className="font-body text-xs text-stone-500 mt-1">
                              Position: {image.position} | Scale: {image.scale}
                            </p>
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditImage(image)}
                              className="p-2 text-stone-500 hover:text-[#B8A88A]"
                              title="Edit"
                            >
                              <PencilSimple size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteImage(image.id)}
                              className="p-2 text-stone-500 hover:text-red-500"
                              title="Delete"
                            >
                              <Trash size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Position Guide */}
                <div className="p-4 border-t border-stone-200 bg-stone-50">
                  <h4 className="font-body text-sm font-medium text-stone-700 mb-2">Position Guide</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-stone-500">
                    <div>
                      <strong>Horizontal:</strong><br/>
                      0% = Left, 50% = Center, 100% = Right
                    </div>
                    <div>
                      <strong>Vertical:</strong><br/>
                      0% = Top, 50% = Center, 100% = Bottom
                    </div>
                    <div>
                      <strong>Scale:</strong><br/>
                      cover = Fill, contain = Fit, 90% = Zoom out
                    </div>
                    <div>
                      <strong>Example:</strong><br/>
                      "30% 40%" = 30% from left, 40% from top
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Homepage Content Tab */}
            <TabsContent value="homepage">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200">
                  <h3 className="font-display text-lg">Homepage Content</h3>
                  <p className="font-body text-sm text-stone-500">Edit your homepage text and story</p>
                </div>
                
                {homepageContent ? (
                  <div className="p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-body text-sm">Couple Names</Label>
                        <Input
                          value={homepageContent.couple_names || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, couple_names: e.target.value})}
                          className="bg-white border-stone-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body text-sm">Wedding Date</Label>
                        <Input
                          value={homepageContent.wedding_date || ''}
                          onChange={(e) => setHomepageContent({...homepageContent, wedding_date: e.target.value})}
                          className="bg-white border-stone-200"
                        />
                      </div>
                    </div>

                    {/* How We Met */}
                    <div className="border-t pt-6">
                      <h4 className="font-display text-lg mb-4">How We Met / Cómo Nos Conocimos</h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="font-body text-sm">English</Label>
                          <Textarea
                            value={homepageContent.how_we_met || ''}
                            onChange={(e) => setHomepageContent({...homepageContent, how_we_met: e.target.value})}
                            className="bg-white border-stone-200 min-h-[150px]"
                            placeholder="Tell your story..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-sm">Spanish (Español)</Label>
                          <Textarea
                            value={homepageContent.how_we_met_es || ''}
                            onChange={(e) => setHomepageContent({...homepageContent, how_we_met_es: e.target.value})}
                            className="bg-white border-stone-200 min-h-[150px]"
                            placeholder="Cuenta tu historia..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* The Proposal */}
                    <div className="border-t pt-6">
                      <h4 className="font-display text-lg mb-4">The Proposal / La Propuesta</h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="font-body text-sm">English</Label>
                          <Textarea
                            value={homepageContent.proposal || ''}
                            onChange={(e) => setHomepageContent({...homepageContent, proposal: e.target.value})}
                            className="bg-white border-stone-200 min-h-[150px]"
                            placeholder="Tell the proposal story..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-sm">Spanish (Español)</Label>
                          <Textarea
                            value={homepageContent.proposal_es || ''}
                            onChange={(e) => setHomepageContent({...homepageContent, proposal_es: e.target.value})}
                            className="bg-white border-stone-200 min-h-[150px]"
                            placeholder="Cuenta la historia de la propuesta..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Our Forever */}
                    <div className="border-t pt-6">
                      <h4 className="font-display text-lg mb-4">Our Forever / Nuestro Para Siempre</h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="font-body text-sm">English</Label>
                          <Textarea
                            value={homepageContent.our_forever || ''}
                            onChange={(e) => setHomepageContent({...homepageContent, our_forever: e.target.value})}
                            className="bg-white border-stone-200 min-h-[100px]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-sm">Spanish (Español)</Label>
                          <Textarea
                            value={homepageContent.our_forever_es || ''}
                            onChange={(e) => setHomepageContent({...homepageContent, our_forever_es: e.target.value})}
                            className="bg-white border-stone-200 min-h-[100px]"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={() => saveContent('homepage', homepageContent)}
                        disabled={contentLoading}
                        className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                      >
                        {contentLoading ? 'Saving...' : 'Save Homepage Content'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-stone-500">Loading content...</div>
                )}
              </div>
            </TabsContent>

            {/* Itinerary Edit Tab */}
            <TabsContent value="itinerary-edit">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200">
                  <h3 className="font-display text-lg">Itinerary Settings</h3>
                  <p className="font-body text-sm text-stone-500">Edit your wedding weekend schedule</p>
                </div>
                
                {itineraryContent ? (
                  <div className="p-6 space-y-6">
                    {(itineraryContent.days || []).map((day, dayIndex) => (
                      <div key={dayIndex} className="border border-stone-200 rounded-lg overflow-hidden">
                        <div className={`p-4 ${day.is_wedding_day ? 'bg-[#F0EBE3]' : 'bg-stone-50'}`}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="font-body text-sm">Day (English)</Label>
                              <Input
                                value={day.date || ''}
                                onChange={(e) => {
                                  const newDays = [...itineraryContent.days];
                                  newDays[dayIndex].date = e.target.value;
                                  setItineraryContent({...itineraryContent, days: newDays});
                                }}
                                className="bg-white border-stone-200"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-sm">Day (Spanish)</Label>
                              <Input
                                value={day.date_es || ''}
                                onChange={(e) => {
                                  const newDays = [...itineraryContent.days];
                                  newDays[dayIndex].date_es = e.target.value;
                                  setItineraryContent({...itineraryContent, days: newDays});
                                }}
                                className="bg-white border-stone-200"
                              />
                            </div>
                          </div>
                          <div className="mt-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={day.is_wedding_day || false}
                                onChange={(e) => {
                                  const newDays = [...itineraryContent.days];
                                  newDays[dayIndex].is_wedding_day = e.target.checked;
                                  setItineraryContent({...itineraryContent, days: newDays});
                                }}
                                className="rounded border-stone-300"
                              />
                              <span className="text-sm text-stone-600">Wedding Day</span>
                            </label>
                          </div>
                        </div>
                        
                        <div className="p-4 space-y-4">
                          {(day.events || []).map((event, eventIndex) => (
                            <div key={eventIndex} className="border border-stone-100 rounded p-4 bg-stone-50">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div className="space-y-2">
                                  <Label className="font-body text-xs">Time</Label>
                                  <Input
                                    value={event.time || ''}
                                    onChange={(e) => {
                                      const newDays = [...itineraryContent.days];
                                      newDays[dayIndex].events[eventIndex].time = e.target.value;
                                      setItineraryContent({...itineraryContent, days: newDays});
                                    }}
                                    placeholder="6:00 PM"
                                    className="bg-white border-stone-200"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="font-body text-xs">Title (English)</Label>
                                  <Input
                                    value={event.title || ''}
                                    onChange={(e) => {
                                      const newDays = [...itineraryContent.days];
                                      newDays[dayIndex].events[eventIndex].title = e.target.value;
                                      setItineraryContent({...itineraryContent, days: newDays});
                                    }}
                                    className="bg-white border-stone-200"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="font-body text-xs">Title (Spanish)</Label>
                                  <Input
                                    value={event.title_es || ''}
                                    onChange={(e) => {
                                      const newDays = [...itineraryContent.days];
                                      newDays[dayIndex].events[eventIndex].title_es = e.target.value;
                                      setItineraryContent({...itineraryContent, days: newDays});
                                    }}
                                    className="bg-white border-stone-200"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="space-y-2">
                                  <Label className="font-body text-xs">Location</Label>
                                  <Input
                                    value={event.location || ''}
                                    onChange={(e) => {
                                      const newDays = [...itineraryContent.days];
                                      newDays[dayIndex].events[eventIndex].location = e.target.value;
                                      setItineraryContent({...itineraryContent, days: newDays});
                                    }}
                                    placeholder="Cardedeu"
                                    className="bg-white border-stone-200"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="font-body text-xs">Dress Code</Label>
                                  <Input
                                    value={event.dress || ''}
                                    onChange={(e) => {
                                      const newDays = [...itineraryContent.days];
                                      newDays[dayIndex].events[eventIndex].dress = e.target.value;
                                      setItineraryContent({...itineraryContent, days: newDays});
                                    }}
                                    placeholder="Black Tie Optional"
                                    className="bg-white border-stone-200"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label className="font-body text-xs">Description (English)</Label>
                                  <Textarea
                                    value={event.description || ''}
                                    onChange={(e) => {
                                      const newDays = [...itineraryContent.days];
                                      newDays[dayIndex].events[eventIndex].description = e.target.value;
                                      setItineraryContent({...itineraryContent, days: newDays});
                                    }}
                                    className="bg-white border-stone-200 min-h-[80px]"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="font-body text-xs">Description (Spanish)</Label>
                                  <Textarea
                                    value={event.description_es || ''}
                                    onChange={(e) => {
                                      const newDays = [...itineraryContent.days];
                                      newDays[dayIndex].events[eventIndex].description_es = e.target.value;
                                      setItineraryContent({...itineraryContent, days: newDays});
                                    }}
                                    className="bg-white border-stone-200 min-h-[80px]"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end mt-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => {
                                    const newDays = [...itineraryContent.days];
                                    newDays[dayIndex].events = newDays[dayIndex].events.filter((_, i) => i !== eventIndex);
                                    setItineraryContent({...itineraryContent, days: newDays});
                                  }}
                                >
                                  <Trash size={14} className="mr-1" />
                                  Remove Event
                                </Button>
                              </div>
                            </div>
                          ))}
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newDays = [...itineraryContent.days];
                              newDays[dayIndex].events.push({
                                time: '',
                                title: '',
                                title_es: '',
                                location: '',
                                description: '',
                                description_es: '',
                                dress: ''
                              });
                              setItineraryContent({...itineraryContent, days: newDays});
                            }}
                            className="w-full border-dashed"
                          >
                            <Plus size={14} className="mr-1" />
                            Add Event to {day.date || 'This Day'}
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      onClick={() => {
                        const newDays = [...(itineraryContent.days || [])];
                        newDays.push({
                          date: '',
                          date_es: '',
                          is_wedding_day: false,
                          events: []
                        });
                        setItineraryContent({...itineraryContent, days: newDays});
                      }}
                      className="w-full border-dashed border-[#C9B99A] text-[#B8A88A]"
                    >
                      <Plus size={16} className="mr-2" />
                      Add Day
                    </Button>

                    {/* Dress Code Guide Section */}
                    <div className="border-t pt-6 mt-6">
                      <h4 className="font-display text-lg mb-4">Dress Code Guide</h4>
                      <p className="font-body text-sm text-stone-500 mb-4">Edit the dress code descriptions shown at the bottom of the itinerary page</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Casual Dress Code */}
                        <div className="border border-stone-200 rounded p-4 bg-stone-50">
                          <h5 className="font-display text-md mb-3 text-[#B8A88A]">Casual</h5>
                          <div className="space-y-3">
                            <div className="space-y-1">
                              <Label className="font-body text-xs">Title</Label>
                              <Input
                                value={itineraryContent.dress_codes?.casual?.title || 'Casual'}
                                onChange={(e) => {
                                  const newDressCodes = {...(itineraryContent.dress_codes || {})};
                                  newDressCodes.casual = {...(newDressCodes.casual || {}), title: e.target.value};
                                  setItineraryContent({...itineraryContent, dress_codes: newDressCodes});
                                }}
                                className="bg-white border-stone-200"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="font-body text-xs">Title (Spanish)</Label>
                              <Input
                                value={itineraryContent.dress_codes?.casual?.title_es || 'Informal'}
                                onChange={(e) => {
                                  const newDressCodes = {...(itineraryContent.dress_codes || {})};
                                  newDressCodes.casual = {...(newDressCodes.casual || {}), title_es: e.target.value};
                                  setItineraryContent({...itineraryContent, dress_codes: newDressCodes});
                                }}
                                className="bg-white border-stone-200"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="font-body text-xs">Description</Label>
                              <Textarea
                                value={itineraryContent.dress_codes?.casual?.description || ''}
                                onChange={(e) => {
                                  const newDressCodes = {...(itineraryContent.dress_codes || {})};
                                  newDressCodes.casual = {...(newDressCodes.casual || {}), description: e.target.value};
                                  setItineraryContent({...itineraryContent, dress_codes: newDressCodes});
                                }}
                                className="bg-white border-stone-200 min-h-[60px]"
                                placeholder="Comfortable wear. Sundresses, linen shirts..."
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="font-body text-xs">Description (Spanish)</Label>
                              <Textarea
                                value={itineraryContent.dress_codes?.casual?.description_es || ''}
                                onChange={(e) => {
                                  const newDressCodes = {...(itineraryContent.dress_codes || {})};
                                  newDressCodes.casual = {...(newDressCodes.casual || {}), description_es: e.target.value};
                                  setItineraryContent({...itineraryContent, dress_codes: newDressCodes});
                                }}
                                className="bg-white border-stone-200 min-h-[60px]"
                                placeholder="Ropa cómoda. Vestidos de verano..."
                              />
                            </div>
                          </div>
                        </div>

                        {/* Black Tie Optional Dress Code */}
                        <div className="border border-stone-200 rounded p-4 bg-stone-50">
                          <h5 className="font-display text-md mb-3 text-stone-700">Black Tie Optional</h5>
                          <div className="space-y-3">
                            <div className="space-y-1">
                              <Label className="font-body text-xs">Title</Label>
                              <Input
                                value={itineraryContent.dress_codes?.black_tie_optional?.title || 'Black Tie Optional'}
                                onChange={(e) => {
                                  const newDressCodes = {...(itineraryContent.dress_codes || {})};
                                  newDressCodes.black_tie_optional = {...(newDressCodes.black_tie_optional || {}), title: e.target.value};
                                  setItineraryContent({...itineraryContent, dress_codes: newDressCodes});
                                }}
                                className="bg-white border-stone-200"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="font-body text-xs">Title (Spanish)</Label>
                              <Input
                                value={itineraryContent.dress_codes?.black_tie_optional?.title_es || 'Etiqueta Opcional'}
                                onChange={(e) => {
                                  const newDressCodes = {...(itineraryContent.dress_codes || {})};
                                  newDressCodes.black_tie_optional = {...(newDressCodes.black_tie_optional || {}), title_es: e.target.value};
                                  setItineraryContent({...itineraryContent, dress_codes: newDressCodes});
                                }}
                                className="bg-white border-stone-200"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="font-body text-xs">Description</Label>
                              <Textarea
                                value={itineraryContent.dress_codes?.black_tie_optional?.description || ''}
                                onChange={(e) => {
                                  const newDressCodes = {...(itineraryContent.dress_codes || {})};
                                  newDressCodes.black_tie_optional = {...(newDressCodes.black_tie_optional || {}), description: e.target.value};
                                  setItineraryContent({...itineraryContent, dress_codes: newDressCodes});
                                }}
                                className="bg-white border-stone-200 min-h-[60px]"
                                placeholder="Elegant formal attire. Floor-length gowns..."
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="font-body text-xs">Description (Spanish)</Label>
                              <Textarea
                                value={itineraryContent.dress_codes?.black_tie_optional?.description_es || ''}
                                onChange={(e) => {
                                  const newDressCodes = {...(itineraryContent.dress_codes || {})};
                                  newDressCodes.black_tie_optional = {...(newDressCodes.black_tie_optional || {}), description_es: e.target.value};
                                  setItineraryContent({...itineraryContent, dress_codes: newDressCodes});
                                }}
                                className="bg-white border-stone-200 min-h-[60px]"
                                placeholder="Atuendo formal elegante. Vestidos largos..."
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={() => saveContent('itinerary', itineraryContent)}
                        disabled={contentLoading}
                        className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                      >
                        {contentLoading ? 'Saving...' : 'Save Itinerary'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-stone-500">Loading itinerary...</div>
                )}
              </div>
            </TabsContent>

            {/* Registry Edit Tab */}
            <TabsContent value="registry-edit">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200">
                  <h3 className="font-display text-lg">Registry Settings</h3>
                  <p className="font-body text-sm text-stone-500">Edit your registry and Venmo information</p>
                </div>
                
                {registryContent ? (
                  <div className="p-6 space-y-6">
                    <div className="space-y-2">
                      <Label className="font-body text-sm">Registry Message (English)</Label>
                      <Textarea
                        value={registryContent.message || ''}
                        onChange={(e) => setRegistryContent({...registryContent, message: e.target.value})}
                        className="bg-white border-stone-200 min-h-[100px]"
                        placeholder="Your message about gifts..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body text-sm">Registry Message (Spanish)</Label>
                      <Textarea
                        value={registryContent.message_es || ''}
                        onChange={(e) => setRegistryContent({...registryContent, message_es: e.target.value})}
                        className="bg-white border-stone-200 min-h-[100px]"
                        placeholder="Tu mensaje sobre regalos..."
                      />
                    </div>
                    
                    <div className="border-t pt-6">
                      <h4 className="font-display text-lg mb-4">Venmo Information</h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="font-body text-sm">Venmo Note (English)</Label>
                          <Textarea
                            value={registryContent.venmo_note || ''}
                            onChange={(e) => setRegistryContent({...registryContent, venmo_note: e.target.value})}
                            className="bg-white border-stone-200 min-h-[80px]"
                            placeholder="For your convenience, you can send monetary gifts through Venmo..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-sm">Venmo Note (Spanish)</Label>
                          <Textarea
                            value={registryContent.venmo_note_es || ''}
                            onChange={(e) => setRegistryContent({...registryContent, venmo_note_es: e.target.value})}
                            className="bg-white border-stone-200 min-h-[80px]"
                            placeholder="Para tu conveniencia, puedes enviar regalos monetarios a través de Venmo..."
                          />
                        </div>
                      </div>
                      <h5 className="font-body text-sm font-medium mt-4 mb-2">Venmo Usernames</h5>
                      <div className="space-y-2">
                        {(registryContent.venmo_usernames || []).map((username, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              value={username}
                              onChange={(e) => {
                                const newUsernames = [...registryContent.venmo_usernames];
                                newUsernames[index] = e.target.value;
                                setRegistryContent({...registryContent, venmo_usernames: newUsernames});
                              }}
                              className="bg-white border-stone-200"
                              placeholder="@username"
                            />
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                const newUsernames = registryContent.venmo_usernames.filter((_, i) => i !== index);
                                setRegistryContent({...registryContent, venmo_usernames: newUsernames});
                              }}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRegistryContent({
                            ...registryContent, 
                            venmo_usernames: [...(registryContent.venmo_usernames || []), '']
                          })}
                          className="mt-2"
                        >
                          <Plus size={16} className="mr-1" /> Add Username
                        </Button>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={() => saveContent('registry', registryContent)}
                        disabled={contentLoading}
                        className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                      >
                        {contentLoading ? 'Saving...' : 'Save Registry Settings'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-stone-500">Loading content...</div>
                )}
              </div>
            </TabsContent>

            {/* RSVP Settings Tab */}
            <TabsContent value="rsvp-edit">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200">
                  <h3 className="font-display text-lg">RSVP Settings</h3>
                  <p className="font-body text-sm text-stone-500">Configure RSVP deadline, contact email, and meal options</p>
                </div>
                
                {rsvpContent ? (
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-body text-sm">RSVP Deadline (English)</Label>
                        <Input
                          value={rsvpContent.deadline || ''}
                          onChange={(e) => setRsvpContent({...rsvpContent, deadline: e.target.value})}
                          className="bg-white border-stone-200"
                          placeholder="October 1, 2026"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body text-sm">RSVP Deadline (Spanish)</Label>
                        <Input
                          value={rsvpContent.deadline_es || ''}
                          onChange={(e) => setRsvpContent({...rsvpContent, deadline_es: e.target.value})}
                          className="bg-white border-stone-200"
                          placeholder="1 de Octubre, 2026"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="font-body text-sm">Contact Email</Label>
                      <Input
                        value={rsvpContent.contact_email || ''}
                        onChange={(e) => setRsvpContent({...rsvpContent, contact_email: e.target.value})}
                        className="bg-white border-stone-200"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div className="border-t pt-6">
                      <h4 className="font-display text-lg mb-4">Meal Options</h4>
                      <div className="space-y-4">
                        {(rsvpContent.meal_options || []).map((meal, index) => (
                          <div key={index} className="p-4 border border-stone-200 bg-stone-50 space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="font-body text-sm font-medium">Option {index + 1}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newMeals = rsvpContent.meal_options.filter((_, i) => i !== index);
                                  setRsvpContent({...rsvpContent, meal_options: newMeals});
                                }}
                                className="text-red-500 hover:text-red-600"
                              >
                                <Trash size={16} />
                              </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                value={meal.name || ''}
                                onChange={(e) => {
                                  const newMeals = [...rsvpContent.meal_options];
                                  newMeals[index].name = e.target.value;
                                  newMeals[index].id = e.target.value.toLowerCase().replace(/\s+/g, '_');
                                  setRsvpContent({...rsvpContent, meal_options: newMeals});
                                }}
                                className="bg-white border-stone-200"
                                placeholder="Name (English)"
                              />
                              <Input
                                value={meal.name_es || ''}
                                onChange={(e) => {
                                  const newMeals = [...rsvpContent.meal_options];
                                  newMeals[index].name_es = e.target.value;
                                  setRsvpContent({...rsvpContent, meal_options: newMeals});
                                }}
                                className="bg-white border-stone-200"
                                placeholder="Name (Spanish)"
                              />
                            </div>
                            <Input
                              value={meal.description || ''}
                              onChange={(e) => {
                                const newMeals = [...rsvpContent.meal_options];
                                newMeals[index].description = e.target.value;
                                setRsvpContent({...rsvpContent, meal_options: newMeals});
                              }}
                              className="bg-white border-stone-200"
                              placeholder="Description"
                            />
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRsvpContent({
                            ...rsvpContent, 
                            meal_options: [...(rsvpContent.meal_options || []), { id: '', name: '', name_es: '', description: '' }]
                          })}
                        >
                          <Plus size={16} className="mr-1" /> Add Meal Option
                        </Button>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={() => saveContent('rsvp', rsvpContent)}
                        disabled={contentLoading}
                        className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                      >
                        {contentLoading ? 'Saving...' : 'Save RSVP Settings'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-stone-500">Loading content...</div>
                )}
              </div>
            </TabsContent>

            {/* Stay (Accommodations) Edit Tab */}
            <TabsContent value="stay-edit">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200">
                  <h3 className="font-display text-lg">Accommodations / Stay Settings</h3>
                  <p className="font-body text-sm text-stone-500">Edit hotel recommendations and accommodation info</p>
                </div>
                
                {stayContent ? (
                  <div className="p-6 space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="font-body text-sm">Page Intro (English)</Label>
                        <Textarea
                          value={stayContent.intro || ''}
                          onChange={(e) => setStayContent({...stayContent, intro: e.target.value})}
                          className="bg-white border-stone-200 min-h-[100px]"
                          placeholder="Information about where to stay..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body text-sm">Page Intro (Spanish)</Label>
                        <Textarea
                          value={stayContent.intro_es || ''}
                          onChange={(e) => setStayContent({...stayContent, intro_es: e.target.value})}
                          className="bg-white border-stone-200 min-h-[100px]"
                          placeholder="Información sobre dónde hospedarse..."
                        />
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-display text-lg">Hotels</h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newHotels = [...(stayContent.hotels || [])];
                            newHotels.push({
                              name: '',
                              name_es: '',
                              description: '',
                              description_es: '',
                              address: '',
                              phone: '',
                              website: '',
                              price_range: '',
                              notes: '',
                              notes_es: '',
                              image: '',
                              image_position: '50% 50%',
                              pdf_url: '',
                              pdf_label: ''
                            });
                            setStayContent({...stayContent, hotels: newHotels});
                          }}
                          className="border-[#C9B99A] text-[#B8A88A]"
                        >
                          <Plus size={14} className="mr-1" />
                          Add Hotel
                        </Button>
                      </div>

                      {(stayContent.hotels || []).map((hotel, index) => (
                        <div key={index} className="border border-stone-200 rounded p-4 mb-4 bg-stone-50">
                          <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-stone-400 hover:text-[#B8A88A] disabled:opacity-30"
                                  disabled={index === 0}
                                  data-testid={`hotel-${index}-move-up`}
                                  onClick={() => {
                                    const newHotels = [...stayContent.hotels];
                                    [newHotels[index - 1], newHotels[index]] = [newHotels[index], newHotels[index - 1]];
                                    setStayContent({...stayContent, hotels: newHotels});
                                  }}
                                >
                                  <ArrowUp size={12} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-stone-400 hover:text-[#B8A88A] disabled:opacity-30"
                                  disabled={index === (stayContent.hotels || []).length - 1}
                                  data-testid={`hotel-${index}-move-down`}
                                  onClick={() => {
                                    const newHotels = [...stayContent.hotels];
                                    [newHotels[index], newHotels[index + 1]] = [newHotels[index + 1], newHotels[index]];
                                    setStayContent({...stayContent, hotels: newHotels});
                                  }}
                                >
                                  <ArrowDown size={12} />
                                </Button>
                              </div>
                              <span className="font-body text-sm font-medium text-stone-700">Hotel {index + 1}{hotel.name ? ` — ${hotel.name}` : ''}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => {
                                const newHotels = stayContent.hotels.filter((_, i) => i !== index);
                                setStayContent({...stayContent, hotels: newHotels});
                              }}
                            >
                              <Trash size={14} />
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Hotel Name</Label>
                              <Input
                                value={hotel.name || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].name = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Price Range</Label>
                              <Input
                                value={hotel.price_range || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].price_range = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200"
                                placeholder="e.g., $150-250/night"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Description (English)</Label>
                              <Textarea
                                value={hotel.description || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].description = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200 min-h-[60px]"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Description (Spanish)</Label>
                              <Textarea
                                value={hotel.description_es || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].description_es = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200 min-h-[60px]"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Website</Label>
                              <Input
                                value={hotel.website || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].website = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200"
                                placeholder="https://..."
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Phone</Label>
                              <Input
                                value={hotel.phone || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].phone = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Category</Label>
                              <Input
                                value={hotel.category || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].category = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200"
                                placeholder="e.g., Primary Venue, Lakefront"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Distance</Label>
                              <Input
                                value={hotel.distance || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].distance = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200"
                                placeholder="e.g., 5 min drive"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Block Code (optional)</Label>
                              <Input
                                value={hotel.block_code || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].block_code = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200"
                                placeholder="e.g., RODRIGUEZ-FERNANDEZ"
                              />
                            </div>
                          </div>
                          <div className="mt-4 space-y-3">
                            <Label className="font-body text-xs flex items-center gap-2">
                              <Image size={14} className="text-[#B8A88A]" />
                              Hotel Photo
                            </Label>
                            {/* Upload + URL input row */}
                            <div className="flex gap-2">
                              <Input
                                value={hotel.image || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].image = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200 flex-1"
                                placeholder="Image URL or upload below"
                                data-testid={`hotel-${index}-image-url`}
                              />
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp,image/gif"
                                  className="hidden"
                                  data-testid={`hotel-${index}-image-upload`}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (file.size > 10 * 1024 * 1024) {
                                      toast.error('Image must be under 10MB');
                                      return;
                                    }
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                      const token = localStorage.getItem('wedding_admin_token');
                                      const res = await axios.post(`${API}/admin/upload-image`, formData, {
                                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                      });
                                      const newHotels = [...stayContent.hotels];
                                      newHotels[index].image = res.data.url;
                                      setStayContent({...stayContent, hotels: newHotels});
                                      toast.success('Image uploaded!');
                                    } catch (err) {
                                      toast.error('Upload failed');
                                    }
                                    e.target.value = '';
                                  }}
                                />
                                <Button type="button" variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A] whitespace-nowrap" asChild>
                                  <span><UploadSimple size={14} className="mr-1" />Upload</span>
                                </Button>
                              </label>
                            </div>

                            {/* Image preview with drag-to-position */}
                            {hotel.image && (
                              <div className="space-y-2">
                                {/* Draggable preview area */}
                                <div
                                  className="relative h-48 w-full overflow-hidden rounded border-2 border-stone-300 bg-stone-100 cursor-crosshair select-none"
                                  data-testid={`hotel-${index}-drag-area`}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    const container = e.currentTarget;
                                    const updatePos = (ev) => {
                                      const rect = container.getBoundingClientRect();
                                      const x = Math.round(Math.min(100, Math.max(0, ((ev.clientX - rect.left) / rect.width) * 100)));
                                      const y = Math.round(Math.min(100, Math.max(0, ((ev.clientY - rect.top) / rect.height) * 100)));
                                      const newHotels = [...stayContent.hotels];
                                      newHotels[index].image_position = `${x}% ${y}%`;
                                      setStayContent({...stayContent, hotels: newHotels});
                                    };
                                    updatePos(e);
                                    const onMove = (ev) => { ev.preventDefault(); updatePos(ev); };
                                    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                                    document.addEventListener('mousemove', onMove);
                                    document.addEventListener('mouseup', onUp);
                                  }}
                                  onTouchStart={(e) => {
                                    const container = e.currentTarget;
                                    const updatePos = (ev) => {
                                      const touch = ev.touches[0];
                                      const rect = container.getBoundingClientRect();
                                      const x = Math.round(Math.min(100, Math.max(0, ((touch.clientX - rect.left) / rect.width) * 100)));
                                      const y = Math.round(Math.min(100, Math.max(0, ((touch.clientY - rect.top) / rect.height) * 100)));
                                      const newHotels = [...stayContent.hotels];
                                      newHotels[index].image_position = `${x}% ${y}%`;
                                      setStayContent({...stayContent, hotels: newHotels});
                                    };
                                    updatePos(e);
                                    const onMove = (ev) => { ev.preventDefault(); updatePos(ev); };
                                    const onEnd = () => { container.removeEventListener('touchmove', onMove); container.removeEventListener('touchend', onEnd); };
                                    container.addEventListener('touchmove', onMove, { passive: false });
                                    container.addEventListener('touchend', onEnd);
                                  }}
                                >
                                  <img
                                    src={hotel.image}
                                    alt={hotel.name || 'Hotel preview'}
                                    className="w-full h-full object-cover pointer-events-none"
                                    style={{ objectPosition: hotel.image_position || '50% 50%' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                    data-testid={`hotel-${index}-image-preview`}
                                  />
                                  {/* Crosshair focal point indicator */}
                                  <div
                                    className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                                    style={{
                                      left: (hotel.image_position || '50% 50%').split(' ')[0],
                                      top: (hotel.image_position || '50% 50%').split(' ')[1] || '50%',
                                    }}
                                    data-testid={`hotel-${index}-crosshair`}
                                  >
                                    <div className="absolute inset-0 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]" />
                                    <div className="absolute top-1/2 left-0 w-full h-[1px] bg-white shadow-[0_0_1px_rgba(0,0,0,0.5)]" />
                                    <div className="absolute left-1/2 top-0 h-full w-[1px] bg-white shadow-[0_0_1px_rgba(0,0,0,0.5)]" />
                                  </div>
                                  {/* Position label */}
                                  <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
                                    {hotel.image_position || '50% 50%'}
                                  </div>
                                  <div className="absolute top-1 left-1 bg-black/50 text-white/80 text-[9px] px-1.5 py-0.5 rounded">
                                    Click or drag to set focal point
                                  </div>
                                </div>
                                {/* Quick presets */}
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <Label className="font-body text-xs text-stone-500">Quick Presets</Label>
                                  </div>
                                  <div className="flex flex-wrap gap-1" data-testid={`hotel-${index}-position-presets`}>
                                    {[
                                      { label: 'Top', value: '50% 0%' },
                                      { label: 'Center', value: '50% 50%' },
                                      { label: 'Bottom', value: '50% 100%' },
                                      { label: 'Left', value: '0% 50%' },
                                      { label: 'Right', value: '100% 50%' },
                                    ].map((preset) => (
                                      <Button
                                        key={preset.value}
                                        type="button"
                                        variant={(hotel.image_position || '50% 50%') === preset.value ? 'default' : 'outline'}
                                        size="sm"
                                        className={`text-[10px] h-6 px-2 ${(hotel.image_position || '50% 50%') === preset.value ? 'bg-[#C9B99A] hover:bg-[#B8A88A] text-white' : 'border-stone-300 text-stone-600'}`}
                                        onClick={() => {
                                          const newHotels = [...stayContent.hotels];
                                          newHotels[index].image_position = preset.value;
                                          setStayContent({...stayContent, hotels: newHotels});
                                        }}
                                      >
                                        {preset.label}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          {/* PDF attachment */}
                          <div className="mt-4 space-y-2">
                            <Label className="font-body text-xs flex items-center gap-2">
                              <FileText size={14} className="text-[#B8A88A]" />
                              Pricing PDF (optional)
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                value={hotel.pdf_url || ''}
                                onChange={(e) => {
                                  const newHotels = [...stayContent.hotels];
                                  newHotels[index].pdf_url = e.target.value;
                                  setStayContent({...stayContent, hotels: newHotels});
                                }}
                                className="bg-white border-stone-200 flex-1"
                                placeholder="PDF URL or upload"
                                data-testid={`hotel-${index}-pdf-url`}
                              />
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="application/pdf"
                                  className="hidden"
                                  data-testid={`hotel-${index}-pdf-upload`}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (file.size > 20 * 1024 * 1024) {
                                      toast.error('PDF must be under 20MB');
                                      return;
                                    }
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                      const token = localStorage.getItem('wedding_admin_token');
                                      const res = await axios.post(`${API}/admin/upload-image`, formData, {
                                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                      });
                                      const newHotels = [...stayContent.hotels];
                                      newHotels[index].pdf_url = res.data.url;
                                      setStayContent({...stayContent, hotels: newHotels});
                                      toast.success('PDF uploaded!');
                                    } catch (err) {
                                      toast.error('Upload failed');
                                    }
                                    e.target.value = '';
                                  }}
                                />
                                <Button type="button" variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A] whitespace-nowrap" asChild>
                                  <span><UploadSimple size={14} className="mr-1" />Upload PDF</span>
                                </Button>
                              </label>
                            </div>
                            {hotel.pdf_url && (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 flex items-center gap-2">
                                  <Input
                                    value={hotel.pdf_label || ''}
                                    onChange={(e) => {
                                      const newHotels = [...stayContent.hotels];
                                      newHotels[index].pdf_label = e.target.value;
                                      setStayContent({...stayContent, hotels: newHotels});
                                    }}
                                    className="bg-white border-stone-200"
                                    placeholder="Button label (e.g., View Cabin Pricing)"
                                    data-testid={`hotel-${index}-pdf-label`}
                                  />
                                </div>
                                <a
                                  href={hotel.pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#B8A88A] hover:text-[#9A8A6A] text-xs underline whitespace-nowrap"
                                >
                                  Preview PDF
                                </a>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                  onClick={() => {
                                    const newHotels = [...stayContent.hotels];
                                    newHotels[index].pdf_url = '';
                                    newHotels[index].pdf_label = '';
                                    setStayContent({...stayContent, hotels: newHotels});
                                  }}
                                >
                                  <X size={12} />
                                </Button>
                              </div>
                            )}
                          </div>
                          </div>
                        </div>
                      ))}

                      {(!stayContent.hotels || stayContent.hotels.length === 0) && (
                        <p className="text-center py-8 text-stone-400">No hotels added yet. Click "Add Hotel" to start.</p>
                      )}
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={() => saveContent('accommodations', stayContent)}
                        disabled={contentLoading}
                        className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                      >
                        {contentLoading ? 'Saving...' : 'Save Stay Settings'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-stone-500">Loading content...</div>
                )}
              </div>
            </TabsContent>

            {/* Travel Edit Tab */}
            <TabsContent value="travel-edit">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200">
                  <h3 className="font-display text-lg">Travel Page Editor</h3>
                  <p className="font-body text-sm text-stone-500">Edit all travel page content</p>
                </div>
                
                {travelContent ? (
                  <div className="p-6 space-y-8">

                    {/* --- INTRO SECTION --- */}
                    <div className="border border-stone-200 rounded p-4">
                      <h4 className="font-display text-base mb-3">Page Intro</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="font-body text-xs">Intro (EN)</Label>
                          <Textarea value={travelContent.intro || ''} onChange={(e) => setTravelContent({...travelContent, intro: e.target.value})}
                            className="bg-white border-stone-200 min-h-[120px] text-sm" placeholder="Welcome intro text..." />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-xs">Intro (ES)</Label>
                          <Textarea value={travelContent.intro_es || ''} onChange={(e) => setTravelContent({...travelContent, intro_es: e.target.value})}
                            className="bg-white border-stone-200 min-h-[120px] text-sm italic" placeholder="Texto de bienvenida..." />
                        </div>
                      </div>
                    </div>

                    {/* --- TRANSPORTATION SECTION --- */}
                    <div className="border border-stone-200 rounded p-4">
                      <h4 className="font-display text-base mb-3 flex items-center gap-2">
                        <Bus size={16} className="text-[#B8A88A]" /> Transportation Bullets
                      </h4>
                      <p className="font-body text-xs text-stone-400 mb-3">Edit the transportation info shown on the page (English + Spanish pairs)</p>
                      {(travelContent.transportation_bullets || []).map((bullet, idx) => (
                        <div key={idx} className="mb-3 p-3 bg-stone-50 rounded border border-stone-100">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-stone-500 font-body">Bullet {idx + 1}</span>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                              onClick={() => {
                                const newBullets = travelContent.transportation_bullets.filter((_, i) => i !== idx);
                                setTravelContent({...travelContent, transportation_bullets: newBullets});
                              }}><X size={12} /></Button>
                          </div>
                          <Textarea value={bullet.en || ''} onChange={(e) => {
                            const newBullets = [...travelContent.transportation_bullets];
                            newBullets[idx] = {...newBullets[idx], en: e.target.value};
                            setTravelContent({...travelContent, transportation_bullets: newBullets});
                          }} className="bg-white border-stone-200 min-h-[60px] mb-2 text-sm" placeholder="English text" />
                          <Textarea value={bullet.es || ''} onChange={(e) => {
                            const newBullets = [...travelContent.transportation_bullets];
                            newBullets[idx] = {...newBullets[idx], es: e.target.value};
                            setTravelContent({...travelContent, transportation_bullets: newBullets});
                          }} className="bg-white border-stone-200 min-h-[60px] text-sm italic" placeholder="Spanish text" />
                        </div>
                      ))}
                      <Button variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A] mt-2"
                        onClick={() => {
                          const newBullets = [...(travelContent.transportation_bullets || []), {en: '', es: ''}];
                          setTravelContent({...travelContent, transportation_bullets: newBullets});
                        }}>+ Add Bullet</Button>
                    </div>

                    {/* --- WEDDING WEEKEND EVENTS --- */}
                    <div className="border border-stone-200 rounded p-4">
                      <h4 className="font-display text-base mb-3 flex items-center gap-2">
                        <CalendarBlank size={16} className="text-[#B8A88A]" /> Wedding Weekend Events
                      </h4>
                      <p className="font-body text-xs text-stone-400 mb-3">Day-by-day schedule shown on the travel page</p>
                      {(travelContent.wedding_weekend_events || []).map((event, idx) => (
                        <div key={idx} className="mb-3 p-3 bg-stone-50 rounded border border-stone-100">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-stone-500 font-body">Event {idx + 1}</span>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                              onClick={() => {
                                const newEvents = travelContent.wedding_weekend_events.filter((_, i) => i !== idx);
                                setTravelContent({...travelContent, wedding_weekend_events: newEvents});
                              }}><X size={12} /></Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <Input value={event.day || ''} onChange={(e) => {
                              const newEvents = [...travelContent.wedding_weekend_events]; newEvents[idx] = {...newEvents[idx], day: e.target.value};
                              setTravelContent({...travelContent, wedding_weekend_events: newEvents});
                            }} className="bg-white border-stone-200 text-sm" placeholder="Day (EN) e.g. Friday, December 11, 2026" />
                            <Input value={event.day_es || ''} onChange={(e) => {
                              const newEvents = [...travelContent.wedding_weekend_events]; newEvents[idx] = {...newEvents[idx], day_es: e.target.value};
                              setTravelContent({...travelContent, wedding_weekend_events: newEvents});
                            }} className="bg-white border-stone-200 text-sm italic" placeholder="Day (ES)" />
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <Input value={event.title || ''} onChange={(e) => {
                              const newEvents = [...travelContent.wedding_weekend_events]; newEvents[idx] = {...newEvents[idx], title: e.target.value};
                              setTravelContent({...travelContent, wedding_weekend_events: newEvents});
                            }} className="bg-white border-stone-200 text-sm" placeholder="Title (EN)" />
                            <Input value={event.title_es || ''} onChange={(e) => {
                              const newEvents = [...travelContent.wedding_weekend_events]; newEvents[idx] = {...newEvents[idx], title_es: e.target.value};
                              setTravelContent({...travelContent, wedding_weekend_events: newEvents});
                            }} className="bg-white border-stone-200 text-sm italic" placeholder="Title (ES)" />
                          </div>
                          <Textarea value={event.note || ''} onChange={(e) => {
                            const newEvents = [...travelContent.wedding_weekend_events]; newEvents[idx] = {...newEvents[idx], note: e.target.value};
                            setTravelContent({...travelContent, wedding_weekend_events: newEvents});
                          }} className="bg-white border-stone-200 min-h-[50px] mb-2 text-sm" placeholder="Details (EN)" />
                          <Textarea value={event.note_es || ''} onChange={(e) => {
                            const newEvents = [...travelContent.wedding_weekend_events]; newEvents[idx] = {...newEvents[idx], note_es: e.target.value};
                            setTravelContent({...travelContent, wedding_weekend_events: newEvents});
                          }} className="bg-white border-stone-200 min-h-[50px] text-sm italic" placeholder="Details (ES)" />
                        </div>
                      ))}
                      <Button variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A] mt-2"
                        onClick={() => {
                          const newEvents = [...(travelContent.wedding_weekend_events || []), {day: '', day_es: '', title: '', title_es: '', note: '', note_es: ''}];
                          setTravelContent({...travelContent, wedding_weekend_events: newEvents});
                        }}>+ Add Event</Button>
                    </div>

                    {/* --- TOUR OPTIONS SECTION --- */}
                    <div className="border border-stone-200 rounded p-4">
                      <h4 className="font-display text-base mb-3 flex items-center gap-2">
                        <MapTrifold size={16} className="text-[#B8A88A]" /> Tour Options
                      </h4>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="font-body text-xs">Tour Catalog PDF URL</Label>
                          <div className="flex gap-2">
                            <Input value={travelContent.tour_pdf_url || ''} onChange={(e) => setTravelContent({...travelContent, tour_pdf_url: e.target.value})}
                              className="bg-white border-stone-200 flex-1" placeholder="PDF URL" />
                            <label className="cursor-pointer">
                              <input type="file" accept="application/pdf" className="hidden" onChange={async (e) => {
                                const file = e.target.files?.[0]; if (!file) return;
                                const formData = new FormData(); formData.append('file', file);
                                try {
                                  const token = localStorage.getItem('wedding_admin_token');
                                  const res = await axios.post(`${API}/admin/upload-image`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }});
                                  setTravelContent({...travelContent, tour_pdf_url: res.data.url});
                                  toast.success('PDF uploaded!');
                                } catch { toast.error('Upload failed'); }
                                e.target.value = '';
                              }} />
                              <Button type="button" variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A]" asChild>
                                <span><UploadSimple size={14} className="mr-1" />Upload</span>
                              </Button>
                            </label>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label className="font-body text-xs">Tour Description (EN)</Label>
                            <Textarea value={travelContent.tour_description || ''} onChange={(e) => setTravelContent({...travelContent, tour_description: e.target.value})}
                              className="bg-white border-stone-200 min-h-[80px] text-sm" placeholder="Tour description..." />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-body text-xs">Tour Description (ES)</Label>
                            <Textarea value={travelContent.tour_description_es || ''} onChange={(e) => setTravelContent({...travelContent, tour_description_es: e.target.value})}
                              className="bg-white border-stone-200 min-h-[80px] text-sm" placeholder="Descripción de tours..." />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="space-y-2">
                            <Label className="font-body text-xs">Contact Phone</Label>
                            <Input value={travelContent.tour_phone || ''} onChange={(e) => setTravelContent({...travelContent, tour_phone: e.target.value})}
                              className="bg-white border-stone-200" placeholder="+503 7820-5478" />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-body text-xs">Contact Email</Label>
                            <Input value={travelContent.tour_email || ''} onChange={(e) => setTravelContent({...travelContent, tour_email: e.target.value})}
                              className="bg-white border-stone-200" placeholder="email@example.com" />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-body text-xs">Contact Instagram</Label>
                            <Input value={travelContent.tour_instagram || ''} onChange={(e) => setTravelContent({...travelContent, tour_instagram: e.target.value})}
                              className="bg-white border-stone-200" placeholder="@handle" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* --- TRAVEL TIPS SECTION --- */}
                    <div className="border border-stone-200 rounded p-4">
                      <h4 className="font-display text-base mb-3 flex items-center gap-2">
                        <Sun size={16} className="text-[#B8A88A]" /> Travel Tips
                      </h4>
                      {(travelContent.travel_tips || []).map((tip, idx) => (
                        <div key={idx} className="mb-3 p-3 bg-stone-50 rounded border border-stone-100">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-stone-500 font-body">Tip {idx + 1}</span>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                              onClick={() => {
                                const newTips = travelContent.travel_tips.filter((_, i) => i !== idx);
                                setTravelContent({...travelContent, travel_tips: newTips});
                              }}><X size={12} /></Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <Input value={tip.title || ''} onChange={(e) => {
                              const newTips = [...travelContent.travel_tips]; newTips[idx] = {...newTips[idx], title: e.target.value};
                              setTravelContent({...travelContent, travel_tips: newTips});
                            }} className="bg-white border-stone-200 text-sm" placeholder="Title (EN)" />
                            <Input value={tip.title_es || ''} onChange={(e) => {
                              const newTips = [...travelContent.travel_tips]; newTips[idx] = {...newTips[idx], title_es: e.target.value};
                              setTravelContent({...travelContent, travel_tips: newTips});
                            }} className="bg-white border-stone-200 text-sm italic" placeholder="Title (ES)" />
                          </div>
                          <Textarea value={tip.content || ''} onChange={(e) => {
                            const newTips = [...travelContent.travel_tips]; newTips[idx] = {...newTips[idx], content: e.target.value};
                            setTravelContent({...travelContent, travel_tips: newTips});
                          }} className="bg-white border-stone-200 min-h-[60px] mb-2 text-sm" placeholder="Content (EN)" />
                          <Textarea value={tip.content_es || ''} onChange={(e) => {
                            const newTips = [...travelContent.travel_tips]; newTips[idx] = {...newTips[idx], content_es: e.target.value};
                            setTravelContent({...travelContent, travel_tips: newTips});
                          }} className="bg-white border-stone-200 min-h-[60px] text-sm italic" placeholder="Content (ES)" />
                        </div>
                      ))}
                      <Button variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A] mt-2"
                        onClick={() => {
                          const newTips = [...(travelContent.travel_tips || []), {title: '', title_es: '', content: '', content_es: ''}];
                          setTravelContent({...travelContent, travel_tips: newTips});
                        }}>+ Add Travel Tip</Button>
                    </div>

                    {/* --- PACKING LIST SECTION --- */}
                    <div className="border border-stone-200 rounded p-4">
                      <h4 className="font-display text-base mb-3 flex items-center gap-2">
                        <CheckSquare size={16} className="text-[#B8A88A]" /> Packing List
                      </h4>
                      {(travelContent.packing_list_items || []).map((item, idx) => (
                        <div key={idx} className="flex gap-2 mb-2 items-start">
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <Input value={item.en || ''} onChange={(e) => {
                              const newItems = [...travelContent.packing_list_items]; newItems[idx] = {...newItems[idx], en: e.target.value};
                              setTravelContent({...travelContent, packing_list_items: newItems});
                            }} className="bg-white border-stone-200 text-sm" placeholder="Item (EN)" />
                            <Input value={item.es || ''} onChange={(e) => {
                              const newItems = [...travelContent.packing_list_items]; newItems[idx] = {...newItems[idx], es: e.target.value};
                              setTravelContent({...travelContent, packing_list_items: newItems});
                            }} className="bg-white border-stone-200 text-sm italic" placeholder="Item (ES)" />
                          </div>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                            onClick={() => {
                              const newItems = travelContent.packing_list_items.filter((_, i) => i !== idx);
                              setTravelContent({...travelContent, packing_list_items: newItems});
                            }}><X size={12} /></Button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A] mt-2"
                        onClick={() => {
                          const newItems = [...(travelContent.packing_list_items || []), {en: '', es: ''}];
                          setTravelContent({...travelContent, packing_list_items: newItems});
                        }}>+ Add Item</Button>
                    </div>

                    {/* --- LOCAL ATTRACTIONS SECTION --- */}
                    <div className="border border-stone-200 rounded p-4">
                      <h4 className="font-display text-base mb-3 flex items-center gap-2">
                        <MapTrifold size={16} className="text-[#B8A88A]" /> Local Attractions
                      </h4>
                      {(travelContent.local_attractions || []).map((attr, idx) => (
                        <div key={idx} className="mb-3 p-3 bg-stone-50 rounded border border-stone-100">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-stone-500 font-body">Attraction {idx + 1}</span>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                              onClick={() => {
                                const newAttrs = travelContent.local_attractions.filter((_, i) => i !== idx);
                                setTravelContent({...travelContent, local_attractions: newAttrs});
                              }}><X size={12} /></Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <Input value={attr.name || ''} onChange={(e) => {
                              const newAttrs = [...travelContent.local_attractions]; newAttrs[idx] = {...newAttrs[idx], name: e.target.value};
                              setTravelContent({...travelContent, local_attractions: newAttrs});
                            }} className="bg-white border-stone-200 text-sm" placeholder="Name (EN)" />
                            <Input value={attr.name_es || ''} onChange={(e) => {
                              const newAttrs = [...travelContent.local_attractions]; newAttrs[idx] = {...newAttrs[idx], name_es: e.target.value};
                              setTravelContent({...travelContent, local_attractions: newAttrs});
                            }} className="bg-white border-stone-200 text-sm italic" placeholder="Name (ES)" />
                          </div>
                          <Textarea value={attr.description || ''} onChange={(e) => {
                            const newAttrs = [...travelContent.local_attractions]; newAttrs[idx] = {...newAttrs[idx], description: e.target.value};
                            setTravelContent({...travelContent, local_attractions: newAttrs});
                          }} className="bg-white border-stone-200 min-h-[50px] mb-2 text-sm" placeholder="Description (EN)" />
                          <Textarea value={attr.description_es || ''} onChange={(e) => {
                            const newAttrs = [...travelContent.local_attractions]; newAttrs[idx] = {...newAttrs[idx], description_es: e.target.value};
                            setTravelContent({...travelContent, local_attractions: newAttrs});
                          }} className="bg-white border-stone-200 min-h-[50px] text-sm italic" placeholder="Description (ES)" />
                        </div>
                      ))}
                      <Button variant="outline" size="sm" className="border-[#C9B99A] text-[#B8A88A] mt-2"
                        onClick={() => {
                          const newAttrs = [...(travelContent.local_attractions || []), {name: '', name_es: '', description: '', description_es: ''}];
                          setTravelContent({...travelContent, local_attractions: newAttrs});
                        }}>+ Add Attraction</Button>
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={() => saveContent('travel', travelContent)}
                        disabled={contentLoading}
                        className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                        data-testid="save-travel-settings"
                      >
                        {contentLoading ? 'Saving...' : 'Save Travel Settings'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-stone-500">Loading content...</div>
                )}
              </div>
            </TabsContent>

            {/* FAQ Edit Tab */}
            <TabsContent value="faq-edit">
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-4 border-b border-stone-200">
                  <h3 className="font-display text-lg">FAQ Settings</h3>
                  <p className="font-body text-sm text-stone-500">Manage frequently asked questions</p>
                </div>
                
                {faqContent ? (
                  <div className="p-6 space-y-6">
                    <div className="flex justify-between items-center">
                      <h4 className="font-display text-lg">Questions & Answers</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newFaqs = [...(faqContent.faqs || [])];
                          newFaqs.push({
                            question: '',
                            question_es: '',
                            answer: '',
                            answer_es: ''
                          });
                          setFaqContent({...faqContent, faqs: newFaqs});
                        }}
                        className="border-[#C9B99A] text-[#B8A88A]"
                      >
                        <Plus size={14} className="mr-1" />
                        Add Question
                      </Button>
                    </div>

                    {(faqContent.faqs || []).map((faq, index) => (
                      <div key={index} className="border border-stone-200 rounded p-4 bg-stone-50">
                        <div className="flex justify-between items-start mb-4">
                          <span className="font-body text-sm font-medium text-stone-700">Question {index + 1}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              const newFaqs = faqContent.faqs.filter((_, i) => i !== index);
                              setFaqContent({...faqContent, faqs: newFaqs});
                            }}
                          >
                            <Trash size={14} />
                          </Button>
                        </div>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Question (English)</Label>
                              <Input
                                value={faq.question || ''}
                                onChange={(e) => {
                                  const newFaqs = [...faqContent.faqs];
                                  newFaqs[index].question = e.target.value;
                                  setFaqContent({...faqContent, faqs: newFaqs});
                                }}
                                className="bg-white border-stone-200"
                                placeholder="What is the dress code?"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Question (Spanish)</Label>
                              <Input
                                value={faq.question_es || ''}
                                onChange={(e) => {
                                  const newFaqs = [...faqContent.faqs];
                                  newFaqs[index].question_es = e.target.value;
                                  setFaqContent({...faqContent, faqs: newFaqs});
                                }}
                                className="bg-white border-stone-200"
                                placeholder="¿Cuál es el código de vestimenta?"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Answer (English)</Label>
                              <Textarea
                                value={faq.answer || ''}
                                onChange={(e) => {
                                  const newFaqs = [...faqContent.faqs];
                                  newFaqs[index].answer = e.target.value;
                                  setFaqContent({...faqContent, faqs: newFaqs});
                                }}
                                className="bg-white border-stone-200 min-h-[80px]"
                                placeholder="The dress code is..."
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-body text-xs">Answer (Spanish)</Label>
                              <Textarea
                                value={faq.answer_es || ''}
                                onChange={(e) => {
                                  const newFaqs = [...faqContent.faqs];
                                  newFaqs[index].answer_es = e.target.value;
                                  setFaqContent({...faqContent, faqs: newFaqs});
                                }}
                                className="bg-white border-stone-200 min-h-[80px]"
                                placeholder="El código de vestimenta es..."
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {(!faqContent.faqs || faqContent.faqs.length === 0) && (
                      <p className="text-center py-8 text-stone-400">No FAQs added yet. Click "Add Question" to start.</p>
                    )}

                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={() => saveContent('faq', faqContent)}
                        disabled={contentLoading}
                        className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
                      >
                        {contentLoading ? 'Saving...' : 'Save FAQ'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-stone-500">Loading content...</div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Add/Edit Image Modal with Visual Repositioning */}
      <Dialog open={showAddImage} onOpenChange={(open) => {
        setShowAddImage(open);
        if (!open) {
          setEditingImage(null);
          setImageForm({ url: '', position: 'center center', scale: 'cover' });
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingImage ? 'Edit Slideshow Image' : 'Add Slideshow Image'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-body text-sm">Image URL *</Label>
              <Input
                value={imageForm.url}
                onChange={(e) => setImageForm({ ...imageForm, url: e.target.value })}
                className="bg-white border-stone-200"
                placeholder="https://..."
              />
            </div>
            
            {imageForm.url && (
              <ImagePositioner
                imageUrl={imageForm.url}
                position={imageForm.position}
                scale={imageForm.scale}
                onPositionChange={(pos) => setImageForm({ ...imageForm, position: pos })}
                onScaleChange={(scale) => setImageForm({ ...imageForm, scale: scale })}
              />
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAddImage(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddImage}
                disabled={isLoading || !imageForm.url}
                className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
              >
                {isLoading ? 'Saving...' : (editingImage ? 'Update Image' : 'Add Image')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Address Modal */}
      <Dialog open={showEditAddress} onOpenChange={setShowEditAddress}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Edit Address & Household Members</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm">First Name *</Label>
                <Input
                  value={addressForm.first_name}
                  onChange={(e) => setAddressForm({ ...addressForm, first_name: e.target.value })}
                  className="bg-white border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">Last Name *</Label>
                <Input
                  value={addressForm.last_name}
                  onChange={(e) => setAddressForm({ ...addressForm, last_name: e.target.value })}
                  className="bg-white border-stone-200"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm">Email *</Label>
                <Input
                  type="email"
                  value={addressForm.email}
                  onChange={(e) => setAddressForm({ ...addressForm, email: e.target.value })}
                  className="bg-white border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">Phone</Label>
                <Input
                  value={addressForm.phone}
                  onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                  className="bg-white border-stone-200"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label className="font-body text-sm">Street Address</Label>
              <Input
                value={addressForm.address_line1}
                onChange={(e) => setAddressForm({ ...addressForm, address_line1: e.target.value })}
                className="bg-white border-stone-200"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-body text-sm">Apt, Suite, Unit</Label>
              <Input
                value={addressForm.address_line2}
                onChange={(e) => setAddressForm({ ...addressForm, address_line2: e.target.value })}
                className="bg-white border-stone-200"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm">City</Label>
                <Input
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  className="bg-white border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">State</Label>
                <Input
                  value={addressForm.state}
                  onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                  className="bg-white border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">ZIP Code</Label>
                <Input
                  value={addressForm.zip_code}
                  onChange={(e) => setAddressForm({ ...addressForm, zip_code: e.target.value })}
                  className="bg-white border-stone-200"
                />
              </div>
            </div>

            {/* Household Members */}
            <div className="border-t border-stone-200 pt-4 mt-4">
              <div className="flex justify-between items-center mb-4">
                <Label className="font-body text-sm font-medium">Household Members</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addHouseholdMember}
                  className="text-[#B8A88A] border-[#C9B99A] hover:bg-[#F0EBE3]"
                >
                  <Plus size={16} className="mr-1" /> Add Member
                </Button>
              </div>

              {addressForm.plus_ones.length === 0 ? (
                <p className="text-sm text-stone-400 italic">No household members added</p>
              ) : (
                <div className="space-y-3">
                  {addressForm.plus_ones.map((member, index) => (
                    <div key={index} className="bg-stone-50 border border-stone-200 p-3 rounded">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-body text-sm font-medium">Member {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeHouseholdMember(index)}
                          className="text-stone-400 hover:text-red-500"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <Label className="font-body text-xs text-stone-500">Name *</Label>
                          <Input
                            value={member.name}
                            onChange={(e) => updateHouseholdMember(index, 'name', e.target.value)}
                            className="bg-white border-stone-200 text-sm"
                            placeholder="Full name"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="font-body text-xs text-stone-500">Email</Label>
                          <Input
                            type="email"
                            value={member.email || ''}
                            onChange={(e) => updateHouseholdMember(index, 'email', e.target.value)}
                            className="bg-white border-stone-200 text-sm"
                            placeholder="Email"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="font-body text-xs text-stone-500">Phone</Label>
                          <Input
                            value={member.phone || ''}
                            onChange={(e) => updateHouseholdMember(index, 'phone', e.target.value)}
                            className="bg-white border-stone-200 text-sm"
                            placeholder="Phone"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditAddress(false);
                  setEditingAddress(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAddress}
                disabled={isLoading}
                className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Address Dialog */}
      <Dialog open={showAddAddress} onOpenChange={setShowAddAddress}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add New Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm">First Name *</Label>
                <Input
                  value={addressForm.first_name}
                  onChange={(e) => setAddressForm({ ...addressForm, first_name: e.target.value })}
                  className="bg-white border-stone-200"
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">Last Name *</Label>
                <Input
                  value={addressForm.last_name}
                  onChange={(e) => setAddressForm({ ...addressForm, last_name: e.target.value })}
                  className="bg-white border-stone-200"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm">Email *</Label>
                <Input
                  type="email"
                  value={addressForm.email}
                  onChange={(e) => setAddressForm({ ...addressForm, email: e.target.value })}
                  className="bg-white border-stone-200"
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">Phone</Label>
                <Input
                  value={addressForm.phone}
                  onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                  className="bg-white border-stone-200"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label className="font-body text-sm">Street Address</Label>
              <Input
                value={addressForm.address_line1}
                onChange={(e) => setAddressForm({ ...addressForm, address_line1: e.target.value })}
                className="bg-white border-stone-200"
                placeholder="123 Main Street"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-body text-sm">Apt, Suite, Unit</Label>
              <Input
                value={addressForm.address_line2}
                onChange={(e) => setAddressForm({ ...addressForm, address_line2: e.target.value })}
                className="bg-white border-stone-200"
                placeholder="Apt 4B (optional)"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm">City</Label>
                <Input
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  className="bg-white border-stone-200"
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">State</Label>
                <Input
                  value={addressForm.state}
                  onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                  className="bg-white border-stone-200"
                  placeholder="State"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">ZIP Code</Label>
                <Input
                  value={addressForm.zip_code}
                  onChange={(e) => setAddressForm({ ...addressForm, zip_code: e.target.value })}
                  className="bg-white border-stone-200"
                  placeholder="12345"
                />
              </div>
            </div>

            {/* Household Members */}
            <div className="border-t border-stone-200 pt-4 mt-4">
              <div className="flex justify-between items-center mb-4">
                <Label className="font-body text-sm font-medium">Household Members</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addHouseholdMember}
                  className="text-[#B8A88A] border-[#C9B99A] hover:bg-[#F0EBE3]"
                >
                  <Plus size={16} className="mr-1" /> Add Member
                </Button>
              </div>

              {addressForm.plus_ones.length === 0 ? (
                <p className="text-sm text-stone-400 italic">No household members added</p>
              ) : (
                <div className="space-y-3">
                  {addressForm.plus_ones.map((member, index) => (
                    <div key={index} className="bg-stone-50 border border-stone-200 p-3 rounded">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-body text-sm font-medium">Member {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeHouseholdMember(index)}
                          className="text-stone-400 hover:text-red-500"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <Label className="font-body text-xs text-stone-500">Name *</Label>
                          <Input
                            value={member.name}
                            onChange={(e) => updateHouseholdMember(index, 'name', e.target.value)}
                            className="bg-white border-stone-200 text-sm"
                            placeholder="Full name"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="font-body text-xs text-stone-500">Email</Label>
                          <Input
                            type="email"
                            value={member.email || ''}
                            onChange={(e) => updateHouseholdMember(index, 'email', e.target.value)}
                            className="bg-white border-stone-200 text-sm"
                            placeholder="Email"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="font-body text-xs text-stone-500">Phone</Label>
                          <Input
                            value={member.phone || ''}
                            onChange={(e) => updateHouseholdMember(index, 'phone', e.target.value)}
                            className="bg-white border-stone-200 text-sm"
                            placeholder="Phone"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
              <Button
                variant="outline"
                onClick={() => setShowAddAddress(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!addressForm.first_name || !addressForm.last_name || !addressForm.email) {
                    toast.error('Please fill in First Name, Last Name, and Email');
                    return;
                  }
                  setIsLoading(true);
                  try {
                    await axios.post(`${API}/admin/addresses`, addressForm, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    toast.success('Address added successfully!');
                    setShowAddAddress(false);
                    fetchDashboardData();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to add address');
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={isLoading}
                className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
              >
                {isLoading ? 'Adding...' : 'Add Address'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit RSVP Dialog */}
      <Dialog open={showEditRsvp} onOpenChange={setShowEditRsvp}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Edit RSVP</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm">First Name</Label>
                <Input
                  value={rsvpForm.first_name}
                  onChange={(e) => setRsvpForm({ ...rsvpForm, first_name: e.target.value })}
                  className="bg-white border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">Last Name</Label>
                <Input
                  value={rsvpForm.last_name}
                  onChange={(e) => setRsvpForm({ ...rsvpForm, last_name: e.target.value })}
                  className="bg-white border-stone-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-body text-sm">Email</Label>
              <Input
                type="email"
                value={rsvpForm.email}
                onChange={(e) => setRsvpForm({ ...rsvpForm, email: e.target.value })}
                className="bg-white border-stone-200"
              />
            </div>

            {/* Attending Status */}
            <div className="p-4 bg-stone-50 border border-stone-200">
              <Label className="font-body text-sm font-medium mb-3 block">Attending Status</Label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setRsvpForm({ ...rsvpForm, attending: true })}
                  className={`flex-1 py-3 px-4 border-2 transition-colors ${
                    rsvpForm.attending 
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                  }`}
                >
                  <Check size={18} className="inline mr-2" />
                  Attending
                </button>
                <button
                  type="button"
                  onClick={() => setRsvpForm({ ...rsvpForm, attending: false })}
                  className={`flex-1 py-3 px-4 border-2 transition-colors ${
                    !rsvpForm.attending 
                      ? 'border-red-500 bg-red-50 text-red-700' 
                      : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                  }`}
                >
                  <X size={18} className="inline mr-2" />
                  Not Attending
                </button>
              </div>
            </div>

            {/* Welcome Party Status */}
            <div className="p-4 bg-stone-50 border border-stone-200">
              <Label className="font-body text-sm font-medium mb-3 block">
                Welcome Party (Dec 10)
                {rsvpForm.attending_welcome_party === null && <span className="text-xs text-amber-600 ml-2">(Not answered)</span>}
              </Label>
              <div className="flex gap-4">
                <button
                  type="button"
                  data-testid="edit-rsvp-welcome-yes"
                  onClick={() => setRsvpForm({ ...rsvpForm, attending_welcome_party: true })}
                  className={`flex-1 py-3 px-4 border-2 transition-colors ${
                    rsvpForm.attending_welcome_party === true
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                  }`}
                >
                  <Check size={18} className="inline mr-2" />
                  Attending
                </button>
                <button
                  type="button"
                  data-testid="edit-rsvp-welcome-no"
                  onClick={() => setRsvpForm({ ...rsvpForm, attending_welcome_party: false })}
                  className={`flex-1 py-3 px-4 border-2 transition-colors ${
                    rsvpForm.attending_welcome_party === false
                      ? 'border-red-500 bg-red-50 text-red-700' 
                      : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                  }`}
                >
                  <X size={18} className="inline mr-2" />
                  Not Attending
                </button>
              </div>
            </div>

            {/* Beach Day Status */}
            <div className="p-4 bg-stone-50 border border-stone-200">
              <Label className="font-body text-sm font-medium mb-3 block">
                Beach Day — El Tunco (Dec 12)
                {rsvpForm.attending_beach_day === null && <span className="text-xs text-amber-600 ml-2">(Not answered)</span>}
              </Label>
              <div className="flex gap-4">
                <button
                  type="button"
                  data-testid="edit-rsvp-beach-yes"
                  onClick={() => setRsvpForm({ ...rsvpForm, attending_beach_day: true })}
                  className={`flex-1 py-3 px-4 border-2 transition-colors ${
                    rsvpForm.attending_beach_day === true
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                  }`}
                >
                  <Check size={18} className="inline mr-2" />
                  Attending
                </button>
                <button
                  type="button"
                  data-testid="edit-rsvp-beach-no"
                  onClick={() => setRsvpForm({ ...rsvpForm, attending_beach_day: false })}
                  className={`flex-1 py-3 px-4 border-2 transition-colors ${
                    rsvpForm.attending_beach_day === false
                      ? 'border-red-500 bg-red-50 text-red-700' 
                      : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                  }`}
                >
                  <X size={18} className="inline mr-2" />
                  Not Attending
                </button>
              </div>
            </div>

            {/* Meal & Preferences */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm">Meal Choice</Label>
                <select
                  value={rsvpForm.meal_choice}
                  onChange={(e) => setRsvpForm({ ...rsvpForm, meal_choice: e.target.value })}
                  className="w-full h-10 px-3 bg-white border border-stone-200 rounded-md text-sm"
                >
                  <option value="">Select meal...</option>
                  <option value="chicken">Chicken</option>
                  <option value="beef">Beef</option>
                  <option value="fish">Fish</option>
                  <option value="vegetarian">Vegetarian</option>
                  <option value="vegan">Vegan</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">Dietary Restrictions</Label>
                <Input
                  value={rsvpForm.dietary_restrictions}
                  onChange={(e) => setRsvpForm({ ...rsvpForm, dietary_restrictions: e.target.value })}
                  className="bg-white border-stone-200"
                  placeholder="Allergies, etc."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-body text-sm">Song Request</Label>
              <Input
                value={rsvpForm.song_request}
                onChange={(e) => setRsvpForm({ ...rsvpForm, song_request: e.target.value })}
                className="bg-white border-stone-200"
                placeholder="Song to play at the reception"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-body text-sm">Message</Label>
              <Textarea
                value={rsvpForm.message}
                onChange={(e) => setRsvpForm({ ...rsvpForm, message: e.target.value })}
                className="bg-white border-stone-200 min-h-[80px]"
                placeholder="Special message from guest"
              />
            </div>

            {/* Plus Ones */}
            <div className="border-t border-stone-200 pt-4 mt-4">
              <div className="flex justify-between items-center mb-4">
                <Label className="font-body text-sm font-medium">Plus Ones / Guests</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRsvpForm({
                      ...rsvpForm,
                      plus_ones: [...rsvpForm.plus_ones, { name: '', attending: true, attending_welcome_party: null, attending_beach_day: null, meal_choice: '', dietary_restrictions: '', song_request: '' }]
                    });
                  }}
                  className="text-[#B8A88A] border-[#C9B99A] hover:bg-[#F0EBE3]"
                >
                  <Plus size={16} className="mr-1" /> Add Guest
                </Button>
              </div>

              {rsvpForm.plus_ones.length === 0 ? (
                <p className="text-sm text-stone-400 italic">No plus ones added</p>
              ) : (
                <div className="space-y-3">
                  {rsvpForm.plus_ones.map((po, index) => (
                    <div key={index} className="bg-stone-50 border border-stone-200 p-3 rounded">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-body text-sm font-medium">Guest {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newPlusOnes = [...rsvpForm.plus_ones];
                            newPlusOnes.splice(index, 1);
                            setRsvpForm({ ...rsvpForm, plus_ones: newPlusOnes });
                          }}
                          className="text-stone-400 hover:text-red-500"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="space-y-1">
                          <Label className="font-body text-xs text-stone-500">Name</Label>
                          <Input
                            value={po.name}
                            onChange={(e) => {
                              const newPlusOnes = [...rsvpForm.plus_ones];
                              newPlusOnes[index].name = e.target.value;
                              setRsvpForm({ ...rsvpForm, plus_ones: newPlusOnes });
                            }}
                            className="bg-white border-stone-200 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="font-body text-xs text-stone-500">Meal</Label>
                          <select
                            value={po.meal_choice || ''}
                            onChange={(e) => {
                              const newPlusOnes = [...rsvpForm.plus_ones];
                              newPlusOnes[index].meal_choice = e.target.value;
                              setRsvpForm({ ...rsvpForm, plus_ones: newPlusOnes });
                            }}
                            className="w-full h-9 px-2 bg-white border border-stone-200 rounded-md text-sm"
                          >
                            <option value="">Select...</option>
                            <option value="chicken">Chicken</option>
                            <option value="beef">Beef</option>
                            <option value="fish">Fish</option>
                            <option value="vegetarian">Vegetarian</option>
                            <option value="vegan">Vegan</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const newPlusOnes = [...rsvpForm.plus_ones];
                            newPlusOnes[index].attending = true;
                            setRsvpForm({ ...rsvpForm, plus_ones: newPlusOnes });
                          }}
                          className={`flex-1 py-1 px-2 text-xs border transition-colors ${
                            po.attending !== false
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-stone-200 bg-white text-stone-500'
                          }`}
                        >
                          <Check size={12} className="inline mr-1" /> Attending
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const newPlusOnes = [...rsvpForm.plus_ones];
                            newPlusOnes[index].attending = false;
                            setRsvpForm({ ...rsvpForm, plus_ones: newPlusOnes });
                          }}
                          className={`flex-1 py-1 px-2 text-xs border transition-colors ${
                            po.attending === false
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-stone-200 bg-white text-stone-500'
                          }`}
                        >
                          <X size={12} className="inline mr-1" /> Declined
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="font-body text-xs text-stone-500 whitespace-nowrap">Welcome Party:</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newPlusOnes = [...rsvpForm.plus_ones];
                            newPlusOnes[index].attending_welcome_party = true;
                            setRsvpForm({ ...rsvpForm, plus_ones: newPlusOnes });
                          }}
                          className={`flex-1 py-1 px-2 text-xs border transition-colors ${
                            po.attending_welcome_party === true
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-stone-200 bg-white text-stone-500'
                          }`}
                        >
                          <Check size={12} className="inline mr-1" /> Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const newPlusOnes = [...rsvpForm.plus_ones];
                            newPlusOnes[index].attending_welcome_party = false;
                            setRsvpForm({ ...rsvpForm, plus_ones: newPlusOnes });
                          }}
                          className={`flex-1 py-1 px-2 text-xs border transition-colors ${
                            po.attending_welcome_party === false
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-stone-200 bg-white text-stone-500'
                          }`}
                        >
                          <X size={12} className="inline mr-1" /> No
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="font-body text-xs text-stone-500 whitespace-nowrap">Beach Day:</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newPlusOnes = [...rsvpForm.plus_ones];
                            newPlusOnes[index].attending_beach_day = true;
                            setRsvpForm({ ...rsvpForm, plus_ones: newPlusOnes });
                          }}
                          className={`flex-1 py-1 px-2 text-xs border transition-colors ${
                            po.attending_beach_day === true
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-stone-200 bg-white text-stone-500'
                          }`}
                        >
                          <Check size={12} className="inline mr-1" /> Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const newPlusOnes = [...rsvpForm.plus_ones];
                            newPlusOnes[index].attending_beach_day = false;
                            setRsvpForm({ ...rsvpForm, plus_ones: newPlusOnes });
                          }}
                          className={`flex-1 py-1 px-2 text-xs border transition-colors ${
                            po.attending_beach_day === false
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-stone-200 bg-white text-stone-500'
                          }`}
                        >
                          <X size={12} className="inline mr-1" /> No
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditRsvp(false);
                  setEditingRsvp(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!editingRsvp) return;
                  setIsLoading(true);
                  try {
                    await axios.put(`${API}/admin/rsvp/${editingRsvp.id}`, rsvpForm, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    toast.success('RSVP updated successfully!');
                    setShowEditRsvp(false);
                    setEditingRsvp(null);
                    fetchDashboardData();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to update RSVP');
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={isLoading}
                className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Guests Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-guests-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Envelope size={22} className="text-[#B8A88A]" />
              Email Guests
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Group Selector */}
            <div>
              <Label className="font-body text-sm font-medium mb-2 block">Send to</Label>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(EMAIL_TEMPLATES).map(([key, t]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`email-group-${key}`}
                    onClick={() => openEmailDialog(key)}
                    className={`px-3 py-2 text-sm font-body border rounded transition-colors ${
                      emailGroup === key
                        ? 'border-[#C9B99A] bg-[#F0EBE3] text-[#8A7A5C] font-medium'
                        : 'border-stone-200 bg-white text-stone-600 hover:border-[#C9B99A]'
                    }`}
                  >
                    {t.label} ({getEmailRecipients(key).length})
                  </button>
                ))}
              </div>
              <p className="font-body text-xs text-stone-400 mt-1">Switching a group resets the message to its template.</p>
            </div>

            {/* Recipients Preview */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="font-body text-sm font-medium">
                  Recipients ({getEmailRecipients(emailGroup).length}) — added as BCC
                </Label>
                <button
                  type="button"
                  data-testid="copy-email-addresses-btn"
                  onClick={() => {
                    const recipients = getEmailRecipients(emailGroup);
                    if (recipients.length === 0) {
                      toast.error('No recipients in this group');
                      return;
                    }
                    navigator.clipboard.writeText(recipients.join(', '));
                    toast.success(`${recipients.length} email address${recipients.length > 1 ? 'es' : ''} copied!`);
                  }}
                  className="flex items-center gap-1 text-xs font-body text-[#B8A88A] hover:text-[#8A7A5C]"
                >
                  <Copy size={14} /> Copy addresses
                </button>
              </div>
              <div className="max-h-24 overflow-y-auto bg-stone-50 border border-stone-200 rounded p-2" data-testid="email-recipients-preview">
                {getEmailRecipients(emailGroup).length === 0 ? (
                  <p className="font-body text-xs text-stone-400 italic">No guests in this group</p>
                ) : (
                  <p className="font-body text-xs text-stone-600 break-all">
                    {getEmailRecipients(emailGroup).join(', ')}
                  </p>
                )}
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-1">
              <Label className="font-body text-sm font-medium">Subject</Label>
              <Input
                data-testid="email-subject-input"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="bg-white border-stone-200"
              />
            </div>

            {/* Body */}
            <div className="space-y-1">
              <Label className="font-body text-sm font-medium">Message</Label>
              <Textarea
                data-testid="email-body-input"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                className="bg-white border-stone-200 min-h-[220px] font-body text-sm"
              />
            </div>

            <p className="font-body text-xs text-stone-400">
              Tip: if your email app doesn't show all the addresses (some apps limit long links), use "Copy addresses" and paste them into the BCC field yourself.
            </p>

            <div className="flex justify-end gap-3 pt-2 border-t border-stone-200">
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
                Cancel
              </Button>
              <Button
                data-testid="open-in-email-btn"
                onClick={() => {
                  const recipients = getEmailRecipients(emailGroup);
                  if (recipients.length === 0) {
                    toast.error('No recipients in this group');
                    return;
                  }
                  const link = `mailto:?bcc=${recipients.join(',')}&subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
                  window.location.href = link;
                }}
                className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
              >
                <Envelope size={18} className="mr-2" />
                Open in My Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
